import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, MapPin, Briefcase, Megaphone, Truck, Folder, MessageCircle,
  Map as MapIcon, Layers, Users, Bell, Menu, MessageSquare, Mail, CheckCircle,
  ArrowRight, FileText, Navigation, ClipboardList, Package, UserCheck, AlertTriangle,
  Play, Archive, List, BarChart, DollarSign, Clock, HeartHandshake, TrendingUp, Building,
  Shield, Grid, UserPlus, Video, Mic, Send, Download, Activity, Star, Settings as SettingsIcon,
  LayoutGrid, Paperclip, Image as ImageIcon, Snowflake, Home as HomeIcon, CalendarDays,
  AlertOctagon, Timer, Forward, SearchCode, PenTool, Calculator, Network, FileCheck,
  Receipt, Server, FileSignature, Minimize2 as Minimize, Maximize2 as Maximize,
  Plus, Filter, ChevronRight, ChevronDown, Camera, X, Phone, MoreVertical, Quote,
  UploadCloud, BrainCircuit, Ruler, Ban, Share2, Zap, Eye, RefreshCw, AlertCircle,
  Edit2, Smartphone, User, Check, FileSearch, Search, Tag, Inbox, Settings, Info, Database, Home
} from 'lucide-react';
import MapEngine from './MapEngine';
import BrandLogo from './BrandLogo';
// ONE canonical phase list shared with MapEngine.jsx — see src/phases.js (no divergent copies).
import { PHASES as CANON_PHASES } from './phases';
import { auth, googleProvider, ensureUserRecord } from './firebase';
import { onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword, signOut } from 'firebase/auth';

// --- AUTH SCREENS (Phase 1.5 Step 1) ---
function LoginScreen() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const google = async () => { setErr(''); setBusy(true); try { await signInWithPopup(auth, googleProvider); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const emailLogin = async (e) => { e.preventDefault(); setErr(''); setBusy(true); try { await signInWithEmailAndPassword(auth, email.trim(), pw); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
      <div className="mb-6"><BrandLogo height={44} /></div>
      <p className="text-gray-500 mb-8 text-sm">Sign in to continue</p>
      <div className="w-full max-w-sm space-y-3">
        <button onClick={google} disabled={busy} className="w-full py-3 rounded-xl font-bold bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center disabled:opacity-50">Continue with Google</button>
        <div className="text-center text-xs text-gray-400">or</div>
        <form onSubmit={emailLogin} className="space-y-3">
          <input type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
          <input type="password" required placeholder="Password" value={pw} onChange={e => setPw(e.target.value)} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
          <button type="submit" disabled={busy} className="w-full py-3 rounded-xl font-bold bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50">Sign in</button>
        </form>
        {err && <p className="text-red-600 text-xs text-center">{err}</p>}
      </div>
    </div>
  );
}

function WorkspaceComingSoon({ role }) {
  const label = role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
      <Briefcase size={48} className="text-blue-500 mb-4" />
      <h1 className="text-xl font-black text-gray-900 mb-2">{label} workspace</h1>
      <p className="text-gray-500 text-sm mb-6 max-w-sm">This workspace is coming soon. You're signed in with the <b>{role}</b> role.</p>
      <button onClick={() => signOut(auth)} className="px-6 py-3 rounded-xl font-bold bg-gray-900 text-white">Sign out</button>
    </div>
  );
}

function PendingScreen({ email }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center">
      <Shield size={48} className="text-amber-500 mb-4" />
      <h1 className="text-xl font-black text-gray-900 mb-2">Account pending</h1>
      <p className="text-gray-500 text-sm mb-1">{email}</p>
      <p className="text-gray-500 text-sm mb-6 max-w-sm">Your account isn't enabled yet. Ask your manager to add you, then sign in again.</p>
      <button onClick={() => signOut(auth)} className="px-6 py-3 rounded-xl font-bold bg-gray-900 text-white">Sign out</button>
    </div>
  );
}

// --- TEMPORARY UI STUBS ---
const ConstructionNetworkVisualizer = () => <div className="p-8">Visualizer Coming Soon</div>;
const CRMCardModal = () => null;
const DeliveryBoard = () => <div className="p-8">Delivery Board Coming Soon</div>;
const TakeoffEstimateModal = () => null;
const UsersFilesView = () => <div className="p-8">Users & Files Coming Soon</div>;
const CommunicationView = () => <div className="p-8">Comms Coming Soon</div>;
const ServicesViewEnhanced = () => <div className="p-8">Services Coming Soon</div>;
const GlobalInbox = () => null;
const EvaluationSortingBoard = () => <div className="p-8">Evaluation Board Coming Soon</div>;
const LeadGenerationSettingsView = () => <div className="p-8">Settings Coming Soon</div>;
const AddNewBusinessModal = () => null;
const SALES_BOARDS_DATA = [];

// ==================== MOCK DATA ====================

const MENU_STRUCTURE = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'data_collection', label: 'In Person Action', subLabel: 'Field View', icon: MapPin },
  { id: 'management', label: 'Management', icon: Briefcase, boards: ['Lead Generation Settings', 'Evaluation & Sorting', 'Parking', 'Enterprise', 'Communication'] },
  { id: 'sales_pr', label: 'Sales & P.R', icon: Megaphone, boards: ['Networking', 'Supply Sales (B2B)', 'Close to Deal', 'Leads in Sales'] },
  { id: 'services', label: 'Services', icon: Truck, boards: [] },
  { id: 'users_files', label: 'Users & Files', icon: Folder, boards: ['Overview', 'Staff', 'Third Party', 'Digital Services'] }
];

const KANBAN_COLUMNS = [
  { id: 'backed', label: 'Backed (Error)', color: 'bg-red-50 text-red-700' },
  { id: 'new_input', label: 'New Input', color: 'bg-blue-50 text-blue-700' },
  { id: 'processed', label: 'Processed Lead', sub: '(Parked)', color: 'bg-orange-50 text-orange-700' },
  { id: 'in_process', label: 'In Process', color: 'bg-white' },
  { id: 'no_answer', label: 'Not Answer', color: 'bg-gray-50' },
  { id: 'sent_info', label: 'Sent Contact Info', color: 'bg-yellow-50' },
  { id: 'to_do', label: 'To Do', color: 'bg-white' },
  { id: 'output', label: 'Output', sub: '(Label Required)', color: 'bg-green-50 text-green-700' },
];

const SALES_COLUMNS = [
  { id: 'sales_input', label: 'Sales Input', sub: 'From Marketing', color: 'bg-blue-50 text-blue-800' },
  { id: 'estimating', label: 'Estimating', sub: '3rd Party Action', color: 'bg-indigo-50 text-indigo-800' },
  { id: 'quote_sent', label: 'Quote Sent', sub: 'Pending Approval', color: 'bg-yellow-50 text-yellow-800' },
  { id: 'follow_up', label: 'Follow Up', sub: 'Marketer Action', color: 'bg-white' },
  { id: 'deal_won', label: 'Deal / Won', sub: 'Contract Signed', color: 'bg-green-100 text-green-800' },
  { id: 'deal_lost', label: 'Lost / No Deal', sub: 'Move to Next', color: 'bg-gray-100 text-gray-500' }
];

const B2B_PIPELINE_COLUMNS = [
  { id: 'prospecting', label: 'Prospecting', color: 'bg-gray-100 text-gray-700' },
  { id: 'pitching', label: 'Pitching Service', color: 'bg-blue-50 text-blue-700' },
  { id: 'negotiation', label: 'Negotiation', color: 'bg-yellow-50 text-yellow-700' },
  { id: 'agreement', label: 'Agreement Sent', color: 'bg-orange-50 text-orange-700' },
  { id: 'config_pending', label: 'Config Pending', color: 'bg-orange-50 text-orange-700' },
  { id: 'live', label: 'Live Service', color: 'bg-green-100 text-green-700' },
];

const MOCK_CARDS = [
  { id: 101, address: '14 Bayview Ridge', name: '14 Bayview Ridge', builder: 'GreenTime', status: 'new_input', contact: 'Mike B.', lastAction: '2d ago', tags: ['High Value'], phase: 'p1', type: 'Residential', value: '$125K' },
  { id: 102, address: '88 York Mills', name: '88 York Mills', builder: 'Elite', status: 'processed', contact: 'Sarah', lastAction: '1mo ago', tags: ['Parked'], phase: 'p2', type: 'Commercial', value: '$340K' },
  { id: 103, address: '50 Leslie St', name: '50 Leslie St', builder: 'Unknown', status: 'backed', contact: '---', lastAction: '1d ago', tags: ['Wrong Number'], issue: 'Evaluate', phase: 'p1', type: 'Residential', value: '$85K' },
  { id: 104, address: '12 Post Rd', name: '12 Post Rd', builder: 'Luxury', status: 'output', contact: 'Davide', lastAction: '1h ago', tags: ['Sold: Estimate'], outcome: 'To Sales', phase: 'p4', type: 'Luxury Home', value: '$550K' },
  { id: 105, address: '22 Forest Hill', name: '22 Forest Hill', builder: 'ModernBuild', status: 'in_process', contact: 'John', lastAction: '3h ago', tags: [], phase: 'p3', type: 'Residential', value: '$220K' },
  { id: 106, address: '45 Rosedale', name: '45 Rosedale', builder: 'Premium', status: 'no_answer', contact: 'Alice', lastAction: '1d ago', tags: ['Follow-up'], phase: 'p2', type: 'Condo Renovation', value: '$180K' },
  { id: 107, address: '99 Bloor St', name: '99 Bloor St', builder: 'Luxury', status: 'sent_info', contact: 'Tom', lastAction: '4h ago', tags: [], phase: 'p5', type: 'High-End Residential', value: '$680K' },
  { id: 108, address: '123 Avenue Rd', name: '123 Avenue Rd', builder: 'Elite', status: 'to_do', contact: 'Emma', lastAction: '6h ago', tags: ['Priority'], phase: 'p4', type: 'Multi-Unit', value: '$1.2M' },
];

const DIGITAL_MARKETING_CARDS = [
  { 
    id: 201, 
    name: 'North York Foundation Ads',
    campaign: 'Google Ads - Phase 1 Focus',
    status: 'in_process',
    business: 'GreenBuild Developments',
    contact: 'Michael Chen',
    email: 'mchen@greenbuild.ca',
    phone: '(416) 555-0123',
    location: 'North York, ON',
    address: '2450 Yonge St',
    targetPhase: 'Phase 1: Foundation',
    lastAction: '2h ago',
    tags: ['High ROI', 'P1 Focus'],
    type: 'PPC',
    adSpend: '$3,200/mo',
    leads: 45,
    qualified: 31,
    converted: 8,
    cpl: '$71',
    roi: '285%',
    impressions: '45.2K',
    clicks: 1847,
    ctr: '4.1%',
    startDate: 'Jan 15, 2026',
    builder: 'GreenBuild',
    projectType: 'Multi-Unit Residential',
    nextFollowUp: 'Feb 20, 2026'
  },
  { 
    id: 202, 
    name: 'Scarborough Framing Leads',
    campaign: 'Facebook - Phase 2 Target',
    status: 'new_input',
    business: 'EastSide Construction',
    contact: 'Sarah Wong',
    email: 'swong@eastsideconstruct.com',
    phone: '(416) 555-0247',
    location: 'Scarborough, ON',
    address: '850 Progress Ave',
    targetPhase: 'Phase 2: Framing',
    lastAction: '4h ago',
    tags: ['New Lead', 'P2 Focus'],
    type: 'Social',
    adSpend: '$1,850/mo',
    leads: 67,
    qualified: 42,
    converted: 12,
    cpl: '$28',
    roi: '412%',
    impressions: '128K',
    clicks: 3214,
    ctr: '2.5%',
    startDate: 'Feb 1, 2026',
    builder: 'EastSide',
    projectType: 'Townhomes',
    nextFollowUp: 'Feb 18, 2026'
  },
  { 
    id: 203, 
    name: 'Downtown MEC Contractors',
    campaign: 'LinkedIn B2B - Phase 3',
    status: 'sent_info',
    business: 'Urban Elite Builders',
    contact: 'David Rodriguez',
    email: 'd.rodriguez@urbanelite.ca',
    phone: '(647) 555-0891',
    location: 'Downtown Toronto, ON',
    address: '88 King St W',
    targetPhase: 'Phase 3: MEC/Drywall',
    lastAction: '1d ago',
    tags: ['B2B', 'P3 Focus', 'High Value'],
    type: 'B2B Social',
    adSpend: '$2,400/mo',
    leads: 34,
    qualified: 28,
    converted: 9,
    cpl: '$71',
    roi: '340%',
    impressions: '18.5K',
    clicks: 892,
    ctr: '4.8%',
    startDate: 'Jan 10, 2026',
    builder: 'Urban Elite',
    projectType: 'Commercial',
    nextFollowUp: 'Feb 19, 2026'
  },
  { 
    id: 204, 
    name: 'Mississauga Finishing Projects',
    campaign: 'Google Local - Phase 4',
    status: 'in_process',
    business: 'WestEnd Premium Homes',
    contact: 'Jennifer Lee',
    email: 'jlee@westendpremium.ca',
    phone: '(905) 555-0456',
    location: 'Mississauga, ON',
    address: '1200 Dundas St E',
    targetPhase: 'Phase 4: Finishing',
    lastAction: '6h ago',
    tags: ['Active', 'P4 Focus', 'Premium'],
    type: 'Local Search',
    adSpend: '$2,800/mo',
    leads: 56,
    qualified: 39,
    converted: 15,
    cpl: '$50',
    roi: '395%',
    impressions: '62.8K',
    clicks: 2156,
    ctr: '3.4%',
    startDate: 'Jan 22, 2026',
    builder: 'WestEnd Premium',
    projectType: 'Luxury Homes',
    nextFollowUp: 'Feb 21, 2026'
  },
  { 
    id: 205, 
    name: 'Etobicoke Landscaping Season',
    campaign: 'Instagram - Phase 5 Focus',
    status: 'output',
    business: 'Green Spaces Toronto',
    contact: 'Tom Anderson',
    email: 'tanderson@greenspaces.ca',
    phone: '(416) 555-0678',
    location: 'Etobicoke, ON',
    address: '3350 Bloor St W',
    targetPhase: 'Phase 5: Landscaping',
    lastAction: '3h ago',
    tags: ['Seasonal', 'P5 Focus', 'Spring Ready'],
    type: 'Social',
    adSpend: '$1,650/mo',
    leads: 89,
    qualified: 67,
    converted: 22,
    cpl: '$19',
    roi: '520%',
    impressions: '215K',
    clicks: 5824,
    ctr: '2.7%',
    startDate: 'Feb 5, 2026',
    builder: 'Green Spaces',
    projectType: 'Residential Landscaping',
    nextFollowUp: 'Feb 22, 2026'
  },
  { 
    id: 206, 
    name: 'Vaughan New Construction',
    campaign: 'SEO Content - Multi-Phase',
    status: 'processed',
    business: 'Northern Builds Inc',
    contact: 'Robert Kim',
    email: 'rkim@northernbuilds.ca',
    phone: '(905) 555-0234',
    location: 'Vaughan, ON',
    address: '7800 Jane St',
    targetPhase: 'Phase 0: Survey',
    lastAction: '1d ago',
    tags: ['Organic', 'P0-P1 Focus'],
    type: 'SEO',
    adSpend: '$3,500/mo',
    leads: 120,
    qualified: 78,
    converted: 18,
    cpl: '$29',
    roi: '445%',
    impressions: '342K',
    clicks: 8965,
    ctr: '2.6%',
    startDate: 'Dec 1, 2025',
    builder: 'Northern Builds',
    projectType: 'New Development',
    nextFollowUp: 'Feb 25, 2026'
  },
  { 
    id: 207, 
    name: 'MLS Renovation Email Series',
    campaign: 'Email Marketing - Renovation',
    status: 'to_do',
    business: 'Multiple MLS Owners',
    contact: 'Campaign List (450)',
    email: 'marketing@bluejaysonair.com',
    phone: '(416) 555-1000',
    location: 'GTA Wide',
    address: 'Various',
    targetPhase: 'MLS: Renovation',
    lastAction: '5h ago',
    tags: ['Email Campaign', 'MLS Focus', 'Nurture'],
    type: 'Email',
    adSpend: '$950/mo',
    leads: 230,
    qualified: 145,
    converted: 28,
    cpl: '$4',
    roi: '890%',
    impressions: '450 emails',
    clicks: 892,
    ctr: '19.8%',
    startDate: 'Jan 1, 2026',
    builder: 'Various',
    projectType: 'MLS Renovations',
    nextFollowUp: 'Feb 19, 2026'
  },
  { 
    id: 208, 
    name: 'Propane Service Retargeting',
    campaign: 'Display Ads - Seasonal',
    status: 'no_answer',
    business: 'Comfort Heat Solutions',
    contact: 'Lisa Patel',
    email: 'lpatel@comfortheat.ca',
    phone: '(905) 555-0789',
    location: 'Brampton, ON',
    address: '450 Bovaird Dr',
    targetPhase: 'Seasonal: Propane',
    lastAction: '2d ago',
    tags: ['Seasonal', 'Winter Service', 'Follow-up'],
    type: 'Display',
    adSpend: '$1,200/mo',
    leads: 67,
    qualified: 51,
    converted: 19,
    cpl: '$18',
    roi: '625%',
    impressions: '198K',
    clicks: 4124,
    ctr: '2.1%',
    startDate: 'Jan 18, 2026',
    builder: 'Comfort Heat',
    projectType: 'Propane Services',
    nextFollowUp: 'Feb 23, 2026'
  },
  { 
    id: 209, 
    name: 'Oakville Premium Finishing',
    campaign: 'YouTube Video - High-End',
    status: 'in_process',
    business: 'Oakville Luxury Homes',
    contact: 'Amanda Foster',
    email: 'afoster@oakvilleluxury.ca',
    phone: '(905) 555-0912',
    location: 'Oakville, ON',
    address: '1450 Lakeshore Rd',
    targetPhase: 'Phase 4: Finishing',
    lastAction: '8h ago',
    tags: ['Video', 'Luxury', 'P4 Focus'],
    type: 'Video',
    adSpend: '$2,100/mo',
    leads: 23,
    qualified: 19,
    converted: 7,
    cpl: '$91',
    roi: '310%',
    impressions: '89.5K',
    clicks: 1247,
    ctr: '1.4%',
    startDate: 'Feb 8, 2026',
    builder: 'Oakville Luxury',
    projectType: 'Luxury Residential',
    nextFollowUp: 'Feb 24, 2026'
  },
  { 
    id: 210, 
    name: 'Richmond Hill Foundation Leads',
    campaign: 'Google Display - P1 Target',
    status: 'sent_info',
    business: 'Hill Top Developers',
    contact: 'Kevin Zhang',
    email: 'kzhang@hilltopdev.ca',
    phone: '(905) 555-0345',
    location: 'Richmond Hill, ON',
    address: '9600 Yonge St',
    targetPhase: 'Phase 1: Foundation',
    lastAction: '12h ago',
    tags: ['Display', 'P1 Focus', 'Active'],
    type: 'Display',
    adSpend: '$1,950/mo',
    leads: 52,
    qualified: 38,
    converted: 11,
    cpl: '$37',
    roi: '385%',
    impressions: '156K',
    clicks: 3124,
    ctr: '2.0%',
    startDate: 'Jan 28, 2026',
    builder: 'Hill Top',
    projectType: 'Residential Development',
    nextFollowUp: 'Feb 20, 2026'
  },
];


// ==================== LOGISTICS / DELIVERY DATA ====================
const DELIVERY_ITEMS = [
  {
    id: 'D001',
    taskType: 'Banner Installation',
    deliveryType: 'Install ADV Banner',
    status: 'pending',
    priority: 'high',
    business: 'GreenBuild Developments',
    builder: 'GreenBuild',
    contact: 'Michael Chen',
    phone: '(416) 555-0123',
    email: 'mchen@greenbuild.ca',
    address: '2450 Yonge St, North York, ON',
    location: 'North York',
    area: 'Central Toronto',
    targetPhase: 'Phase 2: Framing',
    currentPhase: 'Phase 1: Foundation',
    coordinates: { lat: 43.7615, lng: -79.4111 },
    scheduledDate: 'Feb 19, 2026',
    scheduledTime: '09:00 AM',
    estimatedDuration: '45 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Blue Jays Banner (4x8ft)', 'Installation Kit', 'Safety Gear'],
    itemCount: 3,
    notes: 'Install on north fence facing street. Site manager: John',
    specialInstructions: 'Must wear hard hat. Sign-in at office trailer.',
    completedAt: null,
    routeOrder: 1,
    distance: '12.5 km',
    tags: ['Phase Target', 'High Visibility', 'Marketing']
  },
  {
    id: 'D002',
    taskType: 'Document Delivery',
    deliveryType: 'Envelopes & Documents',
    status: 'in_progress',
    priority: 'urgent',
    business: 'EastSide Construction',
    builder: 'EastSide',
    contact: 'Sarah Wong',
    phone: '(416) 555-0247',
    email: 'swong@eastsideconstruct.com',
    address: '850 Progress Ave, Scarborough, ON',
    location: 'Scarborough',
    area: 'East GTA',
    targetPhase: 'Phase 3: MEC/Drywall',
    currentPhase: 'Phase 3: MEC/Drywall',
    coordinates: { lat: 43.7842, lng: -79.2312 },
    scheduledDate: 'Feb 18, 2026',
    scheduledTime: '10:30 AM',
    estimatedDuration: '20 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Contract Envelope (Urgent)', 'MEC Quote Package', 'Site Plans'],
    itemCount: 3,
    notes: 'Requires signature from Sarah Wong. Contract is time-sensitive.',
    specialInstructions: 'Call ahead 15 min before arrival.',
    completedAt: null,
    routeOrder: 2,
    distance: '18.3 km',
    tags: ['Contract', 'Signature Required', 'Phase 3']
  },
  {
    id: 'D003',
    taskType: 'Marketing Material',
    deliveryType: 'Brochures & Flyers',
    status: 'pending',
    priority: 'medium',
    business: 'Urban Elite Builders',
    builder: 'Urban Elite',
    contact: 'David Rodriguez',
    phone: '(647) 555-0891',
    email: 'd.rodriguez@urbanelite.ca',
    address: '88 King St W, Downtown Toronto, ON',
    location: 'Downtown Toronto',
    area: 'Downtown Core',
    targetPhase: 'Phase 4: Finishing',
    currentPhase: 'Phase 3: MEC/Drywall',
    coordinates: { lat: 43.6487, lng: -79.3845 },
    scheduledDate: 'Feb 19, 2026',
    scheduledTime: '02:00 PM',
    estimatedDuration: '30 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Service Brochures (100 pcs)', 'Business Cards (500 pcs)', 'Promotional Flyers'],
    itemCount: 3,
    notes: 'Leave at main office reception if contact not available.',
    specialInstructions: 'Underground parking - validate ticket at reception.',
    completedAt: null,
    routeOrder: 3,
    distance: '8.7 km',
    tags: ['Marketing', 'Drop-off OK', 'Phase 4 Target']
  },
  {
    id: 'D004',
    taskType: 'Banner Installation',
    deliveryType: 'Install ADV Banner',
    status: 'completed',
    priority: 'high',
    business: 'WestEnd Premium Homes',
    builder: 'WestEnd Premium',
    contact: 'Jennifer Lee',
    phone: '(905) 555-0456',
    email: 'jlee@westendpremium.ca',
    address: '1200 Dundas St E, Mississauga, ON',
    location: 'Mississauga',
    area: 'West GTA',
    targetPhase: 'Phase 1: Foundation',
    currentPhase: 'Phase 0: Survey',
    coordinates: { lat: 43.6081, lng: -79.5619 },
    scheduledDate: 'Feb 17, 2026',
    scheduledTime: '08:00 AM',
    estimatedDuration: '60 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Blue Jays Banner (6x10ft)', 'Installation Hardware', 'Safety Equipment'],
    itemCount: 3,
    notes: 'Banner installed on main gate. Photo documentation completed.',
    specialInstructions: 'Early morning installation before workers arrive.',
    completedAt: 'Feb 17, 2026 - 08:45 AM',
    routeOrder: null,
    distance: '22.1 km',
    tags: ['Completed', 'Photo Archive', 'Large Banner']
  },
  {
    id: 'D005',
    taskType: 'Sample Drop-off',
    deliveryType: 'Material Samples',
    status: 'scheduled',
    priority: 'medium',
    business: 'Green Spaces Toronto',
    builder: 'Green Spaces',
    contact: 'Tom Anderson',
    phone: '(416) 555-0678',
    email: 'tanderson@greenspaces.ca',
    address: '3350 Bloor St W, Etobicoke, ON',
    location: 'Etobicoke',
    area: 'West Toronto',
    targetPhase: 'Phase 5: Landscaping',
    currentPhase: 'Phase 4: Finishing',
    coordinates: { lat: 43.6454, lng: -79.5154 },
    scheduledDate: 'Feb 20, 2026',
    scheduledTime: '11:00 AM',
    estimatedDuration: '25 min',
    driver: 'Majid Survey',
    driverPhone: '(416) 555-9002',
    items: ['Landscaping Material Samples', 'Color Brochures', 'Quote Package'],
    itemCount: 3,
    notes: 'Tom requested specific pavement samples for client review.',
    specialInstructions: 'Enter through side gate. Ask for Tom at landscape office.',
    completedAt: null,
    routeOrder: 4,
    distance: '15.8 km',
    tags: ['Phase 5', 'Landscaping', 'Sample Request']
  },
  {
    id: 'D006',
    taskType: 'Banner Installation',
    deliveryType: 'Install ADV Banner',
    status: 'pending',
    priority: 'high',
    business: 'Northern Builds Inc',
    builder: 'Northern Builds',
    contact: 'Robert Kim',
    phone: '(905) 555-0234',
    email: 'rkim@northernbuilds.ca',
    address: '7800 Jane St, Vaughan, ON',
    location: 'Vaughan',
    area: 'North GTA',
    targetPhase: 'Phase 2: Framing',
    currentPhase: 'Phase 1: Foundation',
    coordinates: { lat: 43.8229, lng: -79.5236 },
    scheduledDate: 'Feb 19, 2026',
    scheduledTime: '01:00 PM',
    estimatedDuration: '50 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Blue Jays Banner (5x9ft)', 'Mounting Brackets', 'Tools & Hardware'],
    itemCount: 3,
    notes: 'Large development site. Install on main construction fence.',
    specialInstructions: 'Site has security gate - call Robert 10 min before arrival.',
    completedAt: null,
    routeOrder: 5,
    distance: '28.4 km',
    tags: ['Large Site', 'Security Gate', 'Phase 2 Target']
  },
  {
    id: 'D007',
    taskType: 'Document Delivery',
    deliveryType: 'Contract Package',
    status: 'pending',
    priority: 'urgent',
    business: 'Hill Top Developers',
    builder: 'Hill Top',
    contact: 'Kevin Zhang',
    phone: '(905) 555-0345',
    email: 'kzhang@hilltopdev.ca',
    address: '9600 Yonge St, Richmond Hill, ON',
    location: 'Richmond Hill',
    area: 'North GTA',
    targetPhase: 'Phase 1: Foundation',
    currentPhase: 'Phase 1: Foundation',
    coordinates: { lat: 43.8767, lng: -79.4401 },
    scheduledDate: 'Feb 18, 2026',
    scheduledTime: '03:00 PM',
    estimatedDuration: '30 min',
    driver: 'Majid Survey',
    driverPhone: '(416) 555-9002',
    items: ['Foundation Contract (2 copies)', 'Engineering Reports', 'Permit Documents'],
    itemCount: 3,
    notes: 'Kevin needs to review and sign contracts today. Time-critical.',
    specialInstructions: 'Meet at site office trailer. Bring ID for site entry.',
    completedAt: null,
    routeOrder: 6,
    distance: '31.2 km',
    tags: ['Urgent', 'Signature Required', 'Phase 1']
  },
  {
    id: 'D008',
    taskType: 'Equipment Drop-off',
    deliveryType: 'Tools & Equipment',
    status: 'scheduled',
    priority: 'low',
    business: 'Oakville Luxury Homes',
    builder: 'Oakville Luxury',
    contact: 'Amanda Foster',
    phone: '(905) 555-0912',
    email: 'afoster@oakvilleluxury.ca',
    address: '1450 Lakeshore Rd, Oakville, ON',
    location: 'Oakville',
    area: 'West GTA',
    targetPhase: 'Phase 4: Finishing',
    currentPhase: 'Phase 4: Finishing',
    coordinates: { lat: 43.4509, lng: -79.6819 },
    scheduledDate: 'Feb 21, 2026',
    scheduledTime: '10:00 AM',
    estimatedDuration: '40 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Laser Level', 'Finishing Tools', 'Safety Equipment'],
    itemCount: 3,
    notes: 'Tools for finishing crew. Return by Feb 28.',
    specialInstructions: 'Gated community - use call box "Oakville Luxury Site".',
    completedAt: null,
    routeOrder: 7,
    distance: '38.6 km',
    tags: ['Equipment', 'Luxury Project', 'Phase 4']
  },
  {
    id: 'D009',
    taskType: 'Banner Installation',
    deliveryType: 'Install ADV Banner',
    status: 'pending',
    priority: 'high',
    business: 'BuildCo Developments',
    builder: 'BuildCo',
    contact: 'Lisa Martinez',
    phone: '(416) 555-0567',
    email: 'lmartinez@buildco.ca',
    address: '4500 Sheppard Ave E, Scarborough, ON',
    location: 'Scarborough',
    area: 'East GTA',
    targetPhase: 'Phase 3: MEC/Drywall',
    currentPhase: 'Phase 2: Framing',
    coordinates: { lat: 43.7691, lng: -79.2951 },
    scheduledDate: 'Feb 20, 2026',
    scheduledTime: '09:30 AM',
    estimatedDuration: '45 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Blue Jays Banner (4x8ft)', 'Installation Kit', 'Documentation Camera'],
    itemCount: 3,
    notes: 'Install on south-facing fence. Get completion photo for client.',
    specialInstructions: 'Park in visitor lot. Yellow safety vest required.',
    completedAt: null,
    routeOrder: 8,
    distance: '19.7 km',
    tags: ['Photo Required', 'Phase 3 Target', 'East GTA']
  },
  {
    id: 'D010',
    taskType: 'Marketing Material',
    deliveryType: 'Promotional Package',
    status: 'completed',
    priority: 'medium',
    business: 'ModernBuild Corp',
    builder: 'ModernBuild',
    contact: 'John Patterson',
    phone: '(905) 555-0723',
    email: 'jpatterson@modernbuild.ca',
    address: '2200 Eglinton Ave W, Mississauga, ON',
    location: 'Mississauga',
    area: 'West GTA',
    targetPhase: 'Phase 2: Framing',
    currentPhase: 'Phase 2: Framing',
    coordinates: { lat: 43.6145, lng: -79.5931 },
    scheduledDate: 'Feb 16, 2026',
    scheduledTime: '02:30 PM',
    estimatedDuration: '20 min',
    driver: 'Majid Survey',
    driverPhone: '(416) 555-9002',
    items: ['Service Catalog', 'Project Portfolio', 'Business Cards'],
    itemCount: 3,
    notes: 'Delivered to John at site office. Very receptive to services.',
    specialInstructions: 'Main office building, 2nd floor.',
    completedAt: 'Feb 16, 2026 - 02:45 PM',
    routeOrder: null,
    distance: '24.3 km',
    tags: ['Completed', 'Receptive Contact', 'Marketing']
  },
  {
    id: 'D011',
    taskType: 'Document Delivery',
    deliveryType: 'Estimate Package',
    status: 'in_progress',
    priority: 'high',
    business: 'Premium Build Group',
    builder: 'Premium Build',
    contact: 'Rachel Green',
    phone: '(647) 555-0834',
    email: 'rgreen@premiumbuild.ca',
    address: '5600 Yonge St, North York, ON',
    location: 'North York',
    area: 'Central Toronto',
    targetPhase: 'Phase 4: Finishing',
    currentPhase: 'Phase 3: MEC/Drywall',
    coordinates: { lat: 43.7799, lng: -79.4145 },
    scheduledDate: 'Feb 18, 2026',
    scheduledTime: '01:30 PM',
    estimatedDuration: '25 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Finishing Estimate', 'Timeline Document', 'Warranty Information'],
    itemCount: 3,
    notes: 'Rachel needs estimate for board meeting tomorrow.',
    specialInstructions: 'Park underground - take ticket, will be validated.',
    completedAt: null,
    routeOrder: 9,
    distance: '14.2 km',
    tags: ['Time-Sensitive', 'Phase 4', 'Estimate']
  },
  {
    id: 'D012',
    taskType: 'Banner Installation',
    deliveryType: 'Install ADV Banner',
    status: 'scheduled',
    priority: 'medium',
    business: 'Lakeshore Developments',
    builder: 'Lakeshore Dev',
    contact: 'Mike Thompson',
    phone: '(416) 555-0945',
    email: 'mthompson@lakeshoredev.ca',
    address: '1850 Lakeshore Blvd W, Etobicoke, ON',
    location: 'Etobicoke',
    area: 'West Toronto',
    targetPhase: 'Phase 1: Foundation',
    currentPhase: 'Phase 0: Survey',
    coordinates: { lat: 43.6289, lng: -79.4789 },
    scheduledDate: 'Feb 21, 2026',
    scheduledTime: '08:30 AM',
    estimatedDuration: '55 min',
    driver: 'Ken Delivery',
    driverPhone: '(416) 555-9001',
    items: ['Blue Jays Banner (5x10ft)', 'Heavy-Duty Mounts', 'Installation Tools'],
    itemCount: 3,
    notes: 'Waterfront site. Banner visible from main road.',
    specialInstructions: 'Windy location - extra securing required.',
    completedAt: null,
    routeOrder: 10,
    distance: '17.9 km',
    tags: ['Waterfront', 'High Visibility', 'Phase 1 Target']
  },
];

const DELIVERY_ROUTES = {
  'Route A - Central/North': {
    driver: 'Ken Delivery',
    deliveries: ['D001', 'D003', 'D011'],
    totalDistance: '35.4 km',
    estimatedTime: '3h 15min',
    status: 'active',
    color: '#3B82F6'
  },
  'Route B - East GTA': {
    driver: 'Ken Delivery',
    deliveries: ['D002', 'D009'],
    totalDistance: '38.0 km',
    estimatedTime: '2h 45min',
    status: 'active',
    color: '#10B981'
  },
  'Route C - West GTA': {
    driver: 'Majid Survey',
    deliveries: ['D005', 'D008', 'D012'],
    totalDistance: '72.3 km',
    estimatedTime: '4h 30min',
    status: 'scheduled',
    color: '#F59E0B'
  },
  'Route D - North GTA': {
    driver: 'Majid Survey',
    deliveries: ['D006', 'D007'],
    totalDistance: '59.6 km',
    estimatedTime: '3h 45min',
    status: 'scheduled',
    color: '#8B5CF6'
  },
};


const SALES_CARDS = [
  { id: 801, address: '14 Bayview Ridge', name: '14 Bayview Ridge', builder: 'GreenTime', trade: 't2', status: 'sales_input', source: 'Phase 1', value: 12500, contact: 'Mike B.', telemarketerNote: "Mike is very interested. He needs a framing quote by Friday. He mentioned the basement is 2000 sqft.", telemarketerName: "Sarah Jenkins", deadline: "Feb 19, 2026 5:00 PM", jobsiteVisit: "Feb 17, 2026 10:00 AM", negotiation: null, isSales: true, type: 'Framing', thirdPartyVendors: ['Smith Electrical Services', 'Garcia Plumbing Pro'] },
  { id: 802, address: '99 York Mills', name: '99 York Mills', builder: 'Modern Living', trade: 't3', status: 'estimating', source: 'Phase 2', value: 8000, contact: 'Alex', telemarketerNote: "Standard drywall job. Send standard rate.", telemarketerName: "David Ross", deadline: "Feb 20, 2026 3:00 PM", jobsiteVisit: null, negotiation: "Feb 21, 2026 2:00 PM", isSales: true, type: 'Drywall', thirdPartyVendors: ['Chen HVAC Solutions'], quoteRequested: true },
  { id: 803, address: '22 Banbury', name: '22 Banbury', builder: 'Private', trade: 't7', status: 'quote_sent', source: 'MLS', value: 45000, contact: 'Owner', telemarketerNote: "Full gut renovation.", telemarketerName: "Sarah Jenkins", deadline: "Feb 25, 2026 12:00 PM", jobsiteVisit: "Feb 18, 2026 9:00 AM", negotiation: "Feb 22, 2026 3:00 PM", isSales: true, type: 'Full Renovation', thirdPartyVendors: ['Smith Electrical Services', 'Garcia Plumbing Pro', 'Williams Roofing'], quoteReceived: true },
  { id: 804, address: '88 Lawrence Ave', name: '88 Lawrence Ave', builder: 'BuildCo', trade: 't5', status: 'follow_up', source: 'Phase 3', value: 15000, contact: 'Lisa', telemarketerNote: "Needs follow up on MEC quote.", telemarketerName: "Tom Digital", deadline: "Feb 18, 2026 4:00 PM", jobsiteVisit: null, negotiation: null, isSales: true, type: 'MEC', thirdPartyVendors: ['Chen HVAC Solutions'] },
  { id: 805, address: '55 Eglinton', name: '55 Eglinton', builder: 'TopBuild', trade: 't4', status: 'deal_won', source: 'Phase 4', value: 22000, contact: 'Paul', telemarketerNote: "Contract signed!", telemarketerName: "Sarah Jenkins", deadline: "Feb 15, 2026 5:00 PM", jobsiteVisit: "Feb 16, 2026 11:00 AM", negotiation: null, isSales: true, type: 'Finishing', thirdPartyVendors: ['Williams Roofing'], quotesAccepted: true },
  { id: 806, address: '77 Bloor Street', name: '77 Bloor Street', builder: 'Downtown Homes', trade: 't8', status: 'deal_lost', source: 'Phase 5', value: 18000, contact: 'Sarah M.', telemarketerNote: "Price was too high. Lost to competitor.", telemarketerName: "David Ross", deadline: "Feb 12, 2026 2:00 PM", jobsiteVisit: null, negotiation: "Feb 13, 2026 10:00 AM", isSales: true, type: 'HVAC', thirdPartyVendors: ['Chen HVAC Solutions'] },
  { id: 807, address: '45 Parkside Dr', name: '45 Parkside Dr', builder: 'Summit Homes', trade: 't1', status: 'sales_input', source: 'Phase 0', value: 9500, contact: 'John K.', telemarketerNote: "New lead from Phase 0 campaign. Interested in insulation quote.", telemarketerName: "Sarah Jenkins", deadline: "Feb 22, 2026 2:00 PM", jobsiteVisit: "Feb 20, 2026 1:00 PM", negotiation: null, isSales: true, type: 'Insulation', thirdPartyVendors: ['Smith Electrical Services'] },
  { id: 808, address: '123 Summer Lane', name: '123 Summer Lane', builder: 'Private', trade: 't6', status: 'estimating', source: 'Seasonal', value: 28000, contact: 'Emily R.', telemarketerNote: "Seasonal project - deck and patio renovation for summer.", telemarketerName: "Tom Digital", deadline: "Feb 28, 2026 5:00 PM", jobsiteVisit: "Feb 19, 2026 3:00 PM", negotiation: null, isSales: true, type: 'Deck & Patio', thirdPartyVendors: ['Garcia Plumbing Pro', 'Williams Roofing'], quoteRequested: true },
];

const MOCK_USER_DATABASE = [
  { id: 101, name: 'Sarah Jenkins', type: 'Staff', role: 'Telemarketer', board: 'Supply Sales (B2B)', score: 92, balance: 1450.00, payModel: 'Base + Commission', status: 'Active', avatar: 'SJ', files: 12, folders: ['Payroll', 'Contracts', 'Performance Reports', 'Tax Documents'] },
  { id: 102, name: 'David Ross', type: 'Staff', role: 'Telemarketer', board: 'Phase 2: Framing', score: 88, balance: 1200.00, payModel: 'Base + Commission', status: 'Active', avatar: 'DR', files: 8, folders: ['Payroll', 'Contracts', 'Performance Reports', 'Tax Documents'] },
  { id: 103, name: 'Majid Survey', type: 'Staff', role: 'Data Collector (Field)', board: 'Data Collection', score: 95, balance: 320.50, payModel: 'Per KM', status: 'Active', avatar: 'MJ', files: 45, folders: ['Payroll', 'Contracts', 'Mileage Records', 'Field Reports', 'Tax Documents'] },
  { id: 104, name: 'Ken Delivery', type: 'Staff', role: 'Data Collector (Delivery)', board: 'Logistics / Delivery', score: 98, balance: 550.00, payModel: 'Per Delivery', status: 'Active', avatar: 'KD', files: 5, folders: ['Payroll', 'Contracts', 'Delivery Logs', 'Tax Documents'] },
  { id: 105, name: 'Alice Manage', type: 'Staff', role: 'Manager', board: 'Management', score: 100, balance: 4500.00, payModel: 'Monthly + Reward', status: 'Active', avatar: 'AM', files: 120, folders: ['Payroll', 'Contracts', 'Performance Reports', 'Strategic Documents', 'Tax Documents'] },
  { id: 106, name: 'Tom Digital', type: 'Staff', role: 'Digital Marketer', board: 'Sales & Estimates', score: 90, balance: 2100.00, payModel: 'Monthly + Comm', status: 'Active', avatar: 'TD', files: 30, folders: ['Payroll', 'Contracts', 'Campaign Reports', 'Analytics', 'Tax Documents'] },
  { id: 201, name: 'Mega Windows', type: 'Third Party', role: 'Client (Factory)', board: 'Sales & Estimates', score: 85, balance: 5000.00, payModel: 'Deposit/Fee', status: 'Active', configStatus: 'configured', avatar: 'MW', files: 15, folders: ['Invoices', 'Agreements', 'Purchase Orders', 'Project Documents', 'Payment Records'] },
  { id: 202, name: 'ABC Rentals', type: 'Third Party', role: 'Client (Rental)', board: 'Close to Deal', score: 70, balance: 1200.00, payModel: 'Deposit/Fee', status: 'Warning', configStatus: 'pending', avatar: 'AB', files: 8, folders: ['Invoices', 'Agreements', 'Rental Records', 'Payment Records'] },
  { id: 203, name: 'BuildCorp Ltd', type: 'Third Party', role: 'Contractor', board: 'Services', score: 92, balance: 8500.00, payModel: 'Project Based', status: 'Active', configStatus: 'configured', avatar: 'BC', files: 42, folders: ['Agreements', 'Project Documents', 'Certificates', 'Insurance', 'Payment Records', 'Change Orders'] },
  { id: 204, name: 'Smith Construction', type: 'Third Party', role: 'Subcontractor', board: 'Services', score: 88, balance: 3200.00, payModel: 'Project Based', status: 'Active', configStatus: 'configured', avatar: 'SC', files: 28, folders: ['Agreements', 'Project Documents', 'Certificates', 'Insurance', 'Payment Records'] },
  { id: 301, name: 'Google Cloud', type: 'Digital Service', role: 'API Provider', board: 'System', score: 100, balance: -450.00, payModel: 'Usage Cost', status: 'Active', avatar: 'GC', files: 6, folders: ['Service Agreements', 'Invoices', 'API Documentation', 'Usage Reports'] },
  { id: 302, name: 'RingCentral', type: 'Digital Service', role: 'VoIP Provider', board: 'Comms', score: 100, balance: -220.00, payModel: 'Usage Cost', status: 'Active', avatar: 'RC', files: 4, folders: ['Service Agreements', 'Invoices', 'Call Records', 'Usage Reports'] },
  { id: 303, name: 'Stripe', type: 'Digital Service', role: 'Payment Processor', board: 'Finance', score: 100, balance: -180.00, payModel: 'Transaction Fee', status: 'Active', avatar: 'ST', files: 12, folders: ['Service Agreements', 'Invoices', 'Transaction Reports', 'Compliance Documents'] },
];

const NETWORKING_DATA = {
  demand: [
    { id: 1, name: 'John Doe (Owner)', type: 'Home Owner', activeProjects: 1, status: 'Active', nationality: 'Italian', trade: 'Owner' },
    { id: 2, name: 'Modern Homes Inc', type: 'Design & Build', activeProjects: 3, status: 'Planning', nationality: 'Korean', trade: 'General Contractor' },
    { id: 8, name: 'Li Chen Properties', type: 'Developer', activeProjects: 5, status: 'Active', nationality: 'Chinese', trade: 'Developer' },
    { id: 9, name: 'Reza Construction', type: 'Home Owner', activeProjects: 2, status: 'Planning', nationality: 'Persian', trade: 'Owner' },
  ],
  supply: [
    { id: 3, name: 'ABC Rentals', type: 'Rental Store', capacity: 'High', status: 'Lead', nationality: 'Canadian', trade: 'Equipment Rental' },
    { id: 4, name: 'Mega Windows', type: 'Factory', capacity: 'Medium', status: 'Client', nationality: 'Italian', trade: 'Window Installation' },
    { id: 5, name: 'Volt Electric', type: 'Contractor', capacity: 'Medium', trade: 'Electrical', status: 'Vetted', nationality: 'Persian' },
    { id: 10, name: 'Kim Drywall Co', type: 'Contractor', capacity: 'High', status: 'Client', nationality: 'Korean', trade: 'Drywall' },
    { id: 11, name: 'Giovanni Masonry', type: 'Contractor', capacity: 'Medium', status: 'Vetted', nationality: 'Italian', trade: 'Masonry' },
    { id: 12, name: 'Zhang Framing', type: 'Contractor', capacity: 'High', status: 'Client', nationality: 'Chinese', trade: 'Framing' },
    { id: 13, name: 'Carlos Painting', type: 'Contractor', capacity: 'Medium', status: 'Lead', nationality: 'Spanish', trade: 'Painting' },
    { id: 14, name: 'Ahmed Plumbing', type: 'Contractor', capacity: 'High', status: 'Vetted', nationality: 'Persian', trade: 'Plumbing' },
  ],
  real_estate: [
    { id: 6, name: 'Sarah Realtor', type: 'Realtor', agency: 'ReMax', leads: 12, nationality: 'Canadian', trade: 'Real Estate' },
    { id: 7, name: 'Invest Corp', type: 'Booker', leads: 45, nationality: 'Korean', trade: 'Real Estate' },
  ],
};

const B2B_LEADS = [
  { id: 901, name: 'ABC Rentals', type: 'Supply Side', status: 'pitching', value: 'Telemarketing Svc', contact: 'Manager Tom', note: 'Interested in lead generation.', isB2B: true, packages: ['telemarketing', 'digital_marketing'] },
  { id: 902, name: 'Mega Windows', type: 'Factory', status: 'config_pending', value: 'Full Service', contact: 'Owner Jane', note: 'Signed agreement. Needs configuration.', isB2B: true, packages: ['telemarketing', 'digital_marketing', 'delivery'] },
  { id: 903, name: 'Best Tiles', type: 'Showroom', status: 'agreement', value: 'MLS API', contact: 'Unknown', note: 'Drafting Agreement.', isB2B: true, packages: ['digital_marketing'] },
  { id: 904, name: 'Master Drywall', type: 'Contractor', status: 'live', value: 'Lead Gen', contact: 'John D.', note: 'Active Service.', isB2B: true, packages: ['telemarketing', 'in_person_marketing'] },
  { id: 905, name: 'TopSupply Co', type: 'Distributor', status: 'prospecting', value: 'Lead Gen', contact: 'Sarah M.', note: 'Initial contact made.', isB2B: true, packages: ['delivery'] },
  { id: 906, name: 'BuildMart', type: 'Retail', status: 'negotiation', value: 'Marketing Package', contact: 'Mike P.', note: 'Discussing pricing.', isB2B: true, packages: ['digital_marketing', 'in_person_marketing', 'delivery'] },
];

// Pipeline boards are generated from the canonical phase list so they never diverge.
const PHASE_BOARD_COLORS = ['border-slate-400', 'border-blue-500', 'border-indigo-500', 'border-violet-500', 'border-orange-500', 'border-fuchsia-500', 'border-emerald-500', 'border-gray-500'];
const SERVICE_BOARDS = [
  ...CANON_PHASES.map(([id, label], i) => ({ id, name: `${id.toUpperCase()} · ${label}`, type: 'pipeline', color: PHASE_BOARD_COLORS[i] || 'border-gray-500' })),
  { id: 'seasonal', name: 'Seasonal: Propane', type: 'seasonal', icon: Snowflake, color: 'border-cyan-400' },
  { id: 'mls', name: 'MLS: Renovation', type: 'mls', icon: HomeIcon, color: 'border-pink-500' },
  { id: 'delivery', name: 'Logistics / Delivery', type: 'delivery', icon: Truck, color: 'border-slate-800' },
  { id: 'sales_estimates', name: 'Sales & Estimates', type: 'sales_hub', icon: DollarSign, color: 'border-green-700' },
  { id: 'digital_marketing', name: 'Digital Marketing', type: 'marketing', icon: Megaphone, color: 'border-rose-500' },
];

const INITIAL_TASKS = [
  { id: 1, type: 'Route', title: 'Sweep York Mills West', assignee: 'Majid', priority: 'Normal', status: 'Active' },
  { id: 2, type: 'Verification', title: 'Check Builder Info: Leslie St', assignee: 'Auto', priority: 'High', status: 'Pending' },
  { id: 3, type: 'Delivery', title: 'Sign Install at Bayview', assignee: 'Ken', priority: 'Normal', status: 'Active' },
];

const EVALUATION_QUEUE = [
  { id: 901, source: 'Field Data', address: '500 Avenue Rd', info: 'Check Builder Name', status: 'Pending' },
  { id: 902, source: 'Phase Output', address: '99 York Mills', info: 'Verify Completion', status: 'Pending' },
  { id: 903, source: 'MLS Feed', address: '123 Forest Hill', info: 'Invalid Phone Number', status: 'Pending' }
];

const TERMINAL_DATA = [
  { id: 801, project: '18 Bridle Path', address: '18 Bridle Path Rd', business: 'Elite Builders', contact: 'James Wilson', phone: '(416) 555-0100', targetPhase: 'phase0', wakeDate: '2026-04-15', priority: 'medium', nextAction: 'Site Survey Required' },
  { id: 802, project: '50 Leslie St', address: '50 Leslie St', business: 'Downtown Developments', contact: 'Maria Garcia', phone: '(416) 555-0201', targetPhase: 'phase1', wakeDate: '2026-03-25', priority: 'high', nextAction: 'Foundation Estimate' },
  { id: 803, project: '12 Post Rd', address: '12 Post Rd', business: 'North York Construction', contact: 'David Chen', phone: '(416) 555-0302', targetPhase: 'phase2', wakeDate: '2026-03-22', priority: 'urgent', nextAction: 'Framing Quote Ready' },
  { id: 804, project: '88 York Mills', address: '88 York Mills Rd', business: 'Premium Build Co', contact: 'Lisa Johnson', phone: '(416) 555-0403', targetPhase: 'phase3', wakeDate: '2026-04-05', priority: 'medium', nextAction: 'MEC Services Follow-up' },
  { id: 805, project: '225 King St W', address: '225 King St W', business: 'Urban Builders Inc', contact: 'Robert Taylor', phone: '(416) 555-0504', targetPhase: 'phase4', wakeDate: '2026-03-28', priority: 'high', nextAction: 'Finishing Package Review' },
  { id: 806, project: '150 Bloor St', address: '150 Bloor St E', business: 'Central Construction', contact: 'Jennifer Lee', phone: '(416) 555-0605', targetPhase: 'phase5', wakeDate: '2026-04-10', priority: 'low', nextAction: 'Landscaping Proposal' },
  { id: 807, project: '42 Avenue Rd', address: '42 Avenue Rd', business: 'Luxury Homes Ltd', contact: 'Michael Brown', phone: '(416) 555-0706', targetPhase: 'mls', wakeDate: '2026-03-30', priority: 'medium', nextAction: 'Renovation Quote' },
  { id: 808, project: '95 St Clair Ave', address: '95 St Clair Ave W', business: 'Midtown Builders', contact: 'Sarah Martinez', phone: '(416) 555-0807', targetPhase: 'phase0', wakeDate: '2026-04-20', priority: 'low', nextAction: 'Initial Assessment' },
  { id: 809, project: '310 Front St', address: '310 Front St E', business: 'Waterfront Dev', contact: 'Tom Anderson', phone: '(416) 555-0908', targetPhase: 'phase1', wakeDate: '2026-03-23', priority: 'urgent', nextAction: 'Foundation Start' },
  { id: 810, project: '78 Eglinton Ave', address: '78 Eglinton Ave E', business: 'North Build Group', contact: 'Emily White', phone: '(416) 555-1009', targetPhase: 'phase2', wakeDate: '2026-04-08', priority: 'high', nextAction: 'Framing Schedule' },
  { id: 811, project: '520 University', address: '520 University Ave', business: 'Medical District Builders', contact: 'Carlos Rodriguez', phone: '(416) 555-1110', targetPhase: 'phase3', wakeDate: '2026-04-12', priority: 'medium', nextAction: 'MEC Coordination' },
  { id: 812, project: '180 Dundas St', address: '180 Dundas St W', business: 'Downtown Elite', contact: 'Amanda Singh', phone: '(416) 555-1211', targetPhase: 'phase4', wakeDate: '2026-03-26', priority: 'high', nextAction: 'Finishing Selection' },
  { id: 813, project: '66 Isabella St', address: '66 Isabella St', business: 'Boutique Builders', contact: 'John Kim', phone: '(416) 555-1312', targetPhase: 'mls', wakeDate: '2026-04-18', priority: 'medium', nextAction: 'Renovation Planning' }
];

const MOCK_CONTACTS = [
  { id: 1, name: 'Sarah Sales', role: 'Telemarketer', status: 'online', avatar: 'SA' },
  { id: 2, name: 'Majid Survey', role: 'Field Collector', status: 'away', avatar: 'MJ' },
  { id: 3, name: 'Ken Delivery', role: 'Logistics', status: 'online', avatar: 'KD' },
];

const MOCK_MESSAGES = {
  1: [
    { id: 1, sender: 'them', text: 'Hey, did you see the new lead?', time: '10:30 AM' },
    { id: 2, sender: 'me', text: 'Yes! Working on it now.', time: '10:32 AM' }
  ],
  2: [
    { id: 1, sender: 'them', text: 'Route completed successfully', time: '2:15 PM' }
  ],
  3: [
    { id: 1, sender: 'them', text: 'Delivery scheduled for tomorrow', time: '4:00 PM' }
  ]
};

const MOCK_BUSINESS_EMAILS = [
  { id: 1, from: 'john.smith@builderco.com', subject: 'Quote Request for 88 Lawrence Ave', preview: 'Hi, I would like to request a quote for framing work...', time: '2 hours ago', read: false, type: 'inquiry' },
  { id: 2, from: 'procurement@megawindows.com', subject: 'Purchase Order #4521', preview: 'Please find attached the purchase order for the recent...', time: '5 hours ago', read: true, type: 'order' },
  { id: 3, from: 'lisa.chen@topbuild.com', subject: 'Project Timeline Update', preview: 'We need to discuss the timeline for 55 Eglinton project...', time: '1 day ago', read: false, type: 'project' },
  { id: 4, from: 'support@googlecloud.com', subject: 'Monthly Invoice - February 2026', preview: 'Your invoice for February is ready. Amount due: $450.00', time: '2 days ago', read: true, type: 'invoice' },
  { id: 5, from: 'alex.johnson@abcrentals.com', subject: 'Contract Renewal Discussion', preview: 'Our contract is expiring next month. Can we schedule...', time: '3 days ago', read: false, type: 'contract' },
];

const MOCK_WEBSITE_FORMS = [
  { id: 1, type: 'Contact Us', name: 'Michael Rodriguez', email: 'michael.r@email.com', phone: '416-555-0123', message: 'I need a quote for full home renovation', submitted: '3 hours ago', status: 'new' },
  { id: 2, type: 'Quote Request', name: 'Emma Thompson', email: 'emma.t@email.com', phone: '647-555-0456', projectType: 'Framing', address: '99 Maple Street, Toronto', message: 'Looking for framing services for new construction', submitted: '1 day ago', status: 'replied' },
  { id: 3, type: 'Service Inquiry', name: 'David Park', email: 'david.park@email.com', phone: '905-555-0789', message: 'Do you provide HVAC installation?', submitted: '2 days ago', status: 'new' },
];

const MOCK_THIRD_PARTY_REGISTRATIONS = [
  {
    id: 1,
    businessName: 'Elite Electrical Services Inc.',
    businessNumber: 'BN123456789',
    address: '456 Industrial Rd, Toronto, ON M4B 1B3',
    inChargePerson: { name: 'Robert Chen', title: 'Owner', phone: '416-555-1234', email: 'robert@eliteelectrical.com' },
    website: 'www.eliteelectrical.com',
    wsib: 'WSIB-789456123',
    insurance: 'INS-456789123',
    googleBusinessLink: 'https://g.page/elite-electrical',
    paymentInfo: { method: 'Credit Card', monthlyFee: 150.00 },
    termsAccepted: true,
    submittedDate: '2026-02-18 09:30 AM',
    status: 'pending'
  },
  {
    id: 2,
    businessName: 'Premium Plumbing Solutions',
    businessNumber: 'BN987654321',
    address: '789 Commerce St, Mississauga, ON L5B 2T4',
    inChargePerson: { name: 'Sarah Mitchell', title: 'Operations Manager', phone: '905-555-5678', email: 'sarah@premiumplumbing.ca' },
    website: 'www.premiumplumbing.ca',
    wsib: 'WSIB-456123789',
    insurance: 'INS-789123456',
    googleBusinessLink: '',
    paymentInfo: { method: 'Bank Transfer', monthlyFee: 150.00 },
    termsAccepted: true,
    submittedDate: '2026-02-17 02:15 PM',
    status: 'pending'
  },
  {
    id: 3,
    businessName: 'Summit Roofing Contractors',
    businessNumber: 'BN456789123',
    address: '321 Builder Ave, Brampton, ON L6T 3W5',
    inChargePerson: { name: 'James Wilson', title: 'President', phone: '647-555-9012', email: 'james@summitroofing.com' },
    website: 'www.summitroofing.com',
    wsib: 'WSIB-321654987',
    insurance: 'INS-654321987',
    googleBusinessLink: 'https://g.page/summit-roofing',
    paymentInfo: { method: 'Credit Card', monthlyFee: 150.00 },
    termsAccepted: true,
    submittedDate: '2026-02-16 11:45 AM',
    status: 'approved'
  },
];

// COMPREHENSIVE BOARD ANALYTICS DATA (must be after all referenced constants)
const BOARD_ANALYTICS = {
  phases: [
    { id: 'p0', name: 'Phase 0: Survey', leadsActive: 45, salesValue: 9000, dealValue: 45000, status: 'active' },
    { id: 'p1', name: 'Phase 1: Foundation', leadsActive: 38, salesValue: 9000, dealValue: 6000, status: 'active' },
    { id: 'p2', name: 'Phase 2: Framing', leadsActive: 52, salesValue: 18000, dealValue: 6000, status: 'active' },
    { id: 'p3', name: 'Phase 3: MEC/Drywall', leadsActive: 41, salesValue: 15000, dealValue: 5000, status: 'active' },
    { id: 'p4', name: 'Phase 4: Finishing', leadsActive: 35, salesValue: 12000, dealValue: 8000, status: 'active' },
    { id: 'p5', name: 'Phase 5: Landscaping', leadsActive: 28, salesValue: 9000, dealValue: 3000, status: 'active' },
    { id: 'p6', name: 'Phase 6: Final', leadsActive: 22, salesValue: 7000, dealValue: 5000, status: 'active' }
  ],
  specialized: [
    { id: 'mls', name: 'MLS: Renovation', leadsActive: 67, salesValue: 28000, dealValue: 5000, status: 'active', icon: HomeIcon },
    { id: 'seasonal', name: 'Seasonal: Propane', leadsActive: 89, salesValue: 15000, dealValue: 5000, status: 'active', icon: Snowflake },
    { id: 'floating', name: 'Floating Support', leadsActive: 34, salesValue: 8000, dealValue: 6000, status: 'active', icon: Layers },
    { id: 'on_job', name: 'On Job Assist', leadsActive: 45, salesValue: 12000, dealValue: 8000, status: 'active', icon: UserCheck },
    { id: 'digital', name: 'Digital Marketing', leadsActive: 120, salesValue: 35000, dealValue: 9000, status: 'active', icon: Zap },
    { id: 'delivery', name: 'Logistics / Delivery', leadsActive: 78, salesValue: 22000, dealValue: 3000, status: 'active', icon: Truck }
  ],
  evaluation: {
    name: 'Evaluation & Sorting',
    leads: EVALUATION_QUEUE.length,
    backedRate: 15,
    status: 'warning'
  },
  terminal: {
    name: 'Parking',
    total: TERMINAL_DATA.length,
    urgent: TERMINAL_DATA.filter(t => t.priority === 'urgent').length,
    high: TERMINAL_DATA.filter(t => t.priority === 'high').length,
    totalValue: 125000
  },
  salesHub: {
    name: 'Sales & Estimates',
    active: SALES_CARDS.length,
    totalValue: SALES_CARDS.reduce((acc, c) => acc + c.value, 0),
    avgDealSize: Math.round(SALES_CARDS.reduce((acc, c) => acc + c.value, 0) / SALES_CARDS.length)
  }
};

// ==================== MAIN APP COMPONENT ====================

// ==================== CARD COMPONENTS ====================

const KanbanCard = ({ data, onClick }) => (
  <div onClick={onClick} className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 mb-2 cursor-pointer hover:shadow-md transition-all group relative">
    <div className={`absolute top-0 bottom-0 left-0 w-1 rounded-l-lg ${data.status === 'backed' ? 'bg-red-500' : data.status === 'output' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
    <div className="pl-3">
      <div className="flex justify-between items-start mb-1">
        <span className="text-[10px] font-bold text-gray-500 uppercase">{data.builder}</span>
        <div className="flex gap-1">
          {(data.tags || []).map((t, i) => <span key={i} className="w-2 h-2 rounded-full bg-blue-200" title={t}></span>)}
        </div>
      </div>
      <div className="font-bold text-sm text-gray-800 mb-1">{data.address}</div>
      <div className="text-xs text-gray-500 mb-2">{data.contact}</div>
      {data.issue && <div className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded mb-2 border border-red-100">Issue: {data.issue}</div>}
      {data.outcome && <div className="text-[10px] bg-green-50 text-green-600 px-2 py-1 rounded mb-2 border border-green-100">Outcome: {data.outcome}</div>}
      <div className="flex justify-between items-center text-[10px] text-gray-400 border-t pt-2 mt-1">
        <span>#{data.id}</span>
        <span>{data.lastAction}</span>
      </div>
    </div>
  </div>
);

const SalesCard = ({ data, onClick, onTakeoffClick }) => {
  // Sales & Estimates card with Takeoff and Invoice features for third-party estimation
  const showAmount = data.status === 'quote_sent' || data.status === 'deal_won' || data.status === 'deal_lost';
  const isEstimating = data.status === 'estimating';
  const isSalesInput = data.status === 'sales_input';
  
  return (
    <div 
      className="bg-white rounded-xl shadow-md border-2 border-slate-200 mb-3 hover:shadow-xl hover:border-orange-400 transition-all group relative overflow-hidden"
    >
      {/* Status Color Bar */}
      <div className={`absolute top-0 bottom-0 left-0 w-1.5 ${
        data.status === 'deal_won' ? 'bg-green-500' :
        data.status === 'deal_lost' ? 'bg-gray-400' :
        data.status === 'estimating' ? 'bg-orange-500' :
        data.status === 'quote_sent' ? 'bg-yellow-500' :
        data.status === 'sales_input' ? 'bg-indigo-500' :
        'bg-blue-500'
      }`}></div>
      
      <div className="p-4 pl-5">
        {/* Header with Job ID and Value */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <div className="bg-orange-100 p-1.5 rounded-lg">
              <Calculator size={15} className="text-orange-600" />
            </div>
            <div>
              <span className="text-[11px] font-bold text-slate-500 uppercase block">Job #{data.id}</span>
              <span className="text-xs font-bold text-orange-600">{data.type || data.trade}</span>
            </div>
          </div>
          {showAmount && (
            <div className="bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
              <div className="text-[10px] text-green-600 font-medium">Quote Value</div>
              <div className="text-sm font-bold text-green-700">${data.value.toLocaleString()}</div>
            </div>
          )}
        </div>
        
        {/* Project Address - Clickable */}
        <div 
          onClick={() => onClick(data)}
          className="font-bold text-base text-gray-900 mb-2 leading-tight cursor-pointer hover:text-orange-600 transition-colors"
        >
          {data.address}
        </div>
        
        {/* Builder/Client Info & Lead Source Badge */}
        <div className="flex items-center gap-2 mb-3 text-sm text-slate-600">
          <Building size={13} className="text-slate-400" />
          <span className="font-medium">{data.builder}</span>
          {data.contact && (
            <>
              <span className="text-slate-300">•</span>
              <span className="text-xs">{data.contact}</span>
            </>
          )}
          {data.source && (
            <>
              <span className="text-slate-300">•</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                data.source.includes('Phase 0') ? 'bg-blue-100 text-blue-700' :
                data.source.includes('Phase 1') ? 'bg-indigo-100 text-indigo-700' :
                data.source.includes('Phase 2') ? 'bg-orange-100 text-orange-700' :
                data.source.includes('Phase 3') ? 'bg-pink-100 text-pink-700' :
                data.source.includes('Phase 4') ? 'bg-rose-100 text-rose-700' :
                data.source.includes('Phase 5') ? 'bg-orange-100 text-orange-700' :
                data.source.includes('Seasonal') ? 'bg-green-100 text-green-700' :
                data.source.includes('MLS') ? 'bg-cyan-100 text-cyan-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {data.source}
              </span>
            </>
          )}
        </div>

        {/* Third Party Vendors - Key Feature for Estimation */}
        {data.thirdPartyVendors && data.thirdPartyVendors.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Users size={12} className="text-orange-600" />
              <span className="text-[10px] font-bold text-orange-700 uppercase">Third Party Estimators</span>
            </div>
            <div className="space-y-1">
              {data.thirdPartyVendors.map((vendor, idx) => (
                <div key={idx} className="text-xs text-orange-900 flex items-center gap-1">
                  <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                  {vendor}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Estimation Timeline */}
        {(data.deadline || data.jobsiteVisit || data.negotiation) && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 mb-3 space-y-1.5">
            {data.jobsiteVisit && (
              <div className="flex items-center gap-2 text-xs">
                <Camera size={12} className="text-blue-600" />
                <span className="font-medium text-slate-700">Site Visit:</span>
                <span className="text-slate-600">{new Date(data.jobsiteVisit).toLocaleDateString()}</span>
              </div>
            )}
            {data.deadline && (
              <div className="flex items-center gap-2 text-xs">
                <Clock size={12} className="text-orange-600" />
                <span className="font-medium text-slate-700">Deadline:</span>
                <span className="text-orange-700 font-bold">{new Date(data.deadline).toLocaleDateString()}</span>
              </div>
            )}
            {data.negotiation && (
              <div className="flex items-center gap-2 text-xs">
                <HeartHandshake size={12} className="text-green-600" />
                <span className="font-medium text-slate-700">Negotiation:</span>
                <span className="text-slate-600">{new Date(data.negotiation).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Telemarketer Note */}
        {data.telemarketerNote && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
            <div className="flex items-center gap-1.5 mb-1">
              <MessageSquare size={11} className="text-blue-600" />
              <span className="text-[10px] font-bold text-blue-700 uppercase">{data.telemarketerName}</span>
            </div>
            <p className="text-xs text-blue-900 italic line-clamp-2">{data.telemarketerNote}</p>
          </div>
        )}
        
        {/* Action Buttons - Estimation Workflow */}
        <div className="flex gap-2 mb-2">
          {/* Third Party View - For viewing plans, takeoff measuring, commenting, and quotations */}
          {(isSalesInput || isEstimating) && data.thirdPartyVendors && data.thirdPartyVendors.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTakeoffClick(data);
              }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-bold text-xs hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
            >
              <Users size={14} />
              Third Party View
            </button>
          )}
          
          {/* Invoice Button - For completed deals */}
          {(data.status === 'quote_sent' || data.status === 'deal_won') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                alert('Invoice generation for third-party coming soon');
              }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold text-xs hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
            >
              <FileText size={14} />
              Generate Invoice
            </button>
          )}
          
          {/* CRM Details Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick(data);
            }}
            className="px-3 py-2.5 bg-indigo-100 text-indigo-700 rounded-lg font-bold text-xs hover:bg-indigo-200 transition-all flex items-center justify-center gap-1.5"
          >
            <Phone size={14} />
            CRM
          </button>
        </div>

        {/* Follow Up Actions - Parking & Move to Deal */}
        {data.status === 'follow_up' && (
          <div className="space-y-2 mb-2">
            {/* Parking & Follow Up Options */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert('Card parked: Not Need - Will be moved to parking/storage');
                }}
                className="flex flex-col items-center justify-center gap-1 px-2 py-2 bg-slate-100 border-2 border-slate-300 text-slate-700 rounded-lg font-bold text-xs hover:bg-slate-200 transition-all"
              >
                <X size={14} />
                <span className="text-[10px]">Not Need</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert('Card parked: Not Answer - Will retry later');
                }}
                className="flex flex-col items-center justify-center gap-1 px-2 py-2 bg-yellow-100 border-2 border-yellow-300 text-yellow-700 rounded-lg font-bold text-xs hover:bg-yellow-200 transition-all"
              >
                <Clock size={14} />
                <span className="text-[10px]">Not Answer</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert('Scheduling in-person follow-up visit...');
                }}
                className="flex flex-col items-center justify-center gap-1 px-2 py-2 bg-blue-100 border-2 border-blue-300 text-blue-700 rounded-lg font-bold text-xs hover:bg-blue-200 transition-all"
              >
                <User size={14} />
                <span className="text-[10px]">In Person</span>
              </button>
            </div>
            
            {/* Move to Deal Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                alert(`Moving to Deal Won! ${
                  data.source?.includes('Phase') ? 
                  `After completion, card will park for ${data.source.replace('Phase ', 'Phase ')} → Phase ${parseInt(data.source.match(/\d+/)?.[0] || 0) + 1}` : 
                  'After completion, card will go to Storage (End of process)'
                }`);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-bold text-xs hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg"
            >
              <TrendingUp size={14} />
              Move to Deal
            </button>
          </div>
        )}

        {/* Deal Won Actions - Agreement & Invoice */}
        {data.status === 'deal_won' && (
          <div className="space-y-2 mb-2">
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert('Opening Agreement Generator...');
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg font-bold text-xs hover:bg-blue-600 transition-all shadow-md"
              >
                <FileText size={12} />
                Agreement
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  alert('Opening Invoice Generator...');
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-500 text-white rounded-lg font-bold text-xs hover:bg-green-600 transition-all shadow-md"
              >
                <DollarSign size={12} />
                Invoice
              </button>
            </div>
            
            {/* Complete Deal & Phase Progression */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const isPhaseCard = data.source?.includes('Phase');
                const currentPhase = parseInt(data.source?.match(/\d+/)?.[0] || 0);
                const nextPhase = currentPhase + 1;
                
                if (isPhaseCard && nextPhase <= 5) {
                  alert(`✅ Deal Complete! Card will park for Phase ${nextPhase} marketing campaign.`);
                } else {
                  alert(`✅ Deal Complete! ${data.source?.includes('MLS') || data.source?.includes('Seasonal') ? 'Card moved to Storage (End of process)' : 'Project finished!'}`);
                }
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-orange-600 text-white rounded-lg font-bold text-xs hover:from-indigo-700 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
            >
              <CheckCircle size={14} />
              {data.source?.includes('Phase') && parseInt(data.source.match(/\d+/)?.[0] || 0) < 5
                ? `Complete & Park for Phase ${parseInt(data.source.match(/\d+/)?.[0] || 0) + 1}`
                : 'Complete & Archive'}
            </button>
          </div>
        )}
        
        {/* Status Badges */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {data.quoteRequested && (
            <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-bold">Quote Requested</span>
          )}
          {data.quoteReceived && (
            <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Quote Received</span>
          )}
          {data.quotesAccepted && (
            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Check size={10} /> Accepted
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const B2BCard = ({ lead, onClick, onConfigure }) => {
  const packageColors = {
    telemarketing: 'bg-blue-100 text-blue-700',
    digital_marketing: 'bg-orange-100 text-orange-700',
    in_person_marketing: 'bg-green-100 text-green-700',
    delivery: 'bg-orange-100 text-orange-700'
  };
  
  const packageLabels = {
    telemarketing: 'TM',
    digital_marketing: 'DM',
    in_person_marketing: 'IP',
    delivery: 'DL'
  };

  return (
    <div onClick={onClick} className="bg-white p-3 rounded-lg shadow-sm border border-slate-200 mb-2 cursor-pointer hover:shadow-md transition-all group relative">
      <div className={`absolute top-0 bottom-0 left-0 w-1 rounded-l-lg ${lead.status === 'prospecting' ? 'bg-slate-400' : lead.status === 'live' ? 'bg-green-500' : lead.status === 'config_pending' ? 'bg-orange-500' : lead.status === 'agreement' ? 'bg-orange-500' : 'bg-blue-500'}`}></div>
      <div className="pl-3">
        <div className="flex justify-between items-start mb-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{lead.type}</span>
          {lead.status === 'config_pending' && (
            <button
              onClick={(e) => { e.stopPropagation(); onConfigure(lead); }}
              className="flex items-center gap-1 text-[10px] bg-green-600 text-white px-2 py-0.5 rounded font-bold animate-pulse hover:bg-green-700 shadow-sm transition-colors"
            >
              <SettingsIcon size={10} /> Configure
            </button>
          )}
        </div>
        <h4 className="font-bold text-sm text-slate-800 mb-1 leading-tight">{lead.name}</h4>
        <div className="text-xs text-slate-500 mb-2 flex flex-col gap-0.5">
          <span>{lead.contact}</span>
          <span className="font-medium text-slate-600">{lead.value}</span>
        </div>
        {lead.packages && lead.packages.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {lead.packages.map(pkg => (
              <span key={pkg} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${packageColors[pkg]}`}>
                {packageLabels[pkg]}
              </span>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center text-[10px] text-slate-400 border-t border-slate-100 pt-2 mt-1">
          <span className="italic truncate max-w-[160px] flex items-center gap-1">
            <MessageSquare size={10} className="text-slate-300"/> "{lead.note}"
          </span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight size={12}/>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MODALS ====================

const TotalLeadsAnalyticsModal = ({ onClose, onNavigate }) => {
  const [activeView, setActiveView] = useState('overview');
  
  console.log('🔴 TotalLeadsAnalyticsModal component rendering!');
  
  const totalLeads = BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.leadsActive, 0) +
                     BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.leadsActive, 0);
  
  const totalSalesValue = BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.salesValue, 0) +
                          BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.salesValue, 0) +
                          BOARD_ANALYTICS.salesHub.totalValue;
  
  const totalDealValue = BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.dealValue, 0) +
                         BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.dealValue, 0);

  console.log('🔴 Modal data calculated - totalLeads:', totalLeads);

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => {
        console.log('🔴 Modal overlay clicked');
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 flex justify-between items-start text-white">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-white/20 p-2 rounded-lg">
                <BarChart size={28} />
              </div>
              <div>
                <h2 className="text-3xl font-bold">Total Leads Analytics</h2>
                <p className="text-blue-100 text-sm">Comprehensive breakdown across all boards & phases</p>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="bg-gradient-to-b from-blue-50 to-white px-8 py-6 border-b">
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-blue-100 p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <List size={20} className="text-blue-600" />
                </div>
                <span className="text-xs font-bold text-gray-500 uppercase">Total Active Leads</span>
              </div>
              <div className="text-3xl font-bold text-gray-900">{totalLeads.toLocaleString()}</div>
              <div className="text-xs text-green-600 font-bold mt-1">↑ 12% vs last month</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-green-100 p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-green-100 p-2 rounded-lg">
                  <DollarSign size={20} className="text-green-600" />
                </div>
                <span className="text-xs font-bold text-gray-500 uppercase">Sales Pipeline Value</span>
              </div>
              <div className="text-3xl font-bold text-gray-900">${totalSalesValue.toLocaleString()}</div>
              <div className="text-xs text-green-600 font-bold mt-1">Active in Sales</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-orange-100 p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <HeartHandshake size={20} className="text-orange-600" />
                </div>
                <span className="text-xs font-bold text-gray-500 uppercase">Deal Value</span>
              </div>
              <div className="text-3xl font-bold text-gray-900">${totalDealValue.toLocaleString()}</div>
              <div className="text-xs text-orange-600 font-bold mt-1">Closed Revenue</div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-orange-100 p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <Archive size={20} className="text-orange-600" />
                </div>
                <span className="text-xs font-bold text-gray-500 uppercase">Parking</span>
              </div>
              <div className="text-3xl font-bold text-gray-900">{BOARD_ANALYTICS.terminal.total}</div>
              <div className="text-xs text-orange-600 font-bold mt-1">${BOARD_ANALYTICS.terminal.totalValue.toLocaleString()} value</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-8 py-3 border-b bg-gray-50 flex gap-6">
          <button
            onClick={() => setActiveView('overview')}
            className={`pb-2 px-1 text-sm font-bold border-b-2 transition-colors ${
              activeView === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutGrid size={16} className="inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveView('phases')}
            className={`pb-2 px-1 text-sm font-bold border-b-2 transition-colors ${
              activeView === 'phases' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Layers size={16} className="inline mr-2" />
            Construction Phases
          </button>
          <button
            onClick={() => setActiveView('specialized')}
            className={`pb-2 px-1 text-sm font-bold border-b-2 transition-colors ${
              activeView === 'specialized' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Star size={16} className="inline mr-2" />
            Specialized Boards
          </button>
          <button
            onClick={() => setActiveView('charts')}
            className={`pb-2 px-1 text-sm font-bold border-b-2 transition-colors ${
              activeView === 'charts' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <TrendingUp size={16} className="inline mr-2" />
            Visual Analytics
          </button>
          <button
            onClick={() => setActiveView('sales')}
            className={`pb-2 px-1 text-sm font-bold border-b-2 transition-colors ${
              activeView === 'sales' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <DollarSign size={16} className="inline mr-2" />
            Sales & Estimates
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          {/* OVERVIEW TAB */}
          {activeView === 'overview' && (
            <div className="grid grid-cols-2 gap-6">
              {/* Construction Phases Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <Layers className="text-indigo-600" size={20} />
                  Construction Waterfall (P0-P6)
                </h3>
                <div className="space-y-3">
                  {BOARD_ANALYTICS.phases.map(phase => (
                    <div key={phase.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer group">
                      <div>
                        <div className="font-bold text-sm text-gray-800">{phase.name}</div>
                        <div className="text-xs text-gray-500">{phase.leadsActive} active leads</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-green-600">${phase.salesValue.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">Sales</div>
                      </div>
                    </div>
                  ))}
                  <div className="pt-3 border-t flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-700">Phase Total:</span>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.leadsActive, 0)} leads
                      </div>
                      <div className="text-sm font-bold text-green-600">
                        ${BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.salesValue, 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Specialized Boards Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <Star className="text-orange-600" size={20} />
                  Specialized & Operations
                </h3>
                <div className="space-y-3">
                  {BOARD_ANALYTICS.specialized.map(board => {
                    const Icon = board.icon;
                    return (
                      <div key={board.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer group">
                        <div className="flex items-center gap-3">
                          <div className="bg-orange-100 p-2 rounded-lg">
                            <Icon size={16} className="text-orange-600" />
                          </div>
                          <div>
                            <div className="font-bold text-sm text-gray-800">{board.name}</div>
                            <div className="text-xs text-gray-500">{board.leadsActive} active leads</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-green-600">${board.salesValue.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">Sales</div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-3 border-t flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-700">Specialized Total:</span>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        {BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.leadsActive, 0)} leads
                      </div>
                      <div className="text-sm font-bold text-green-600">
                        ${BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.salesValue, 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Hub */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="text-green-600" size={20} />
                  Sales & Estimates Hub
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{BOARD_ANALYTICS.salesHub.active}</div>
                    <div className="text-xs text-gray-600 font-bold uppercase">Active Estimates</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-green-600">${BOARD_ANALYTICS.salesHub.totalValue.toLocaleString()}</div>
                    <div className="text-xs text-gray-600 font-bold uppercase">Total Value</div>
                  </div>
                  <div className="col-span-2 pt-4 border-t border-green-200">
                    <div className="text-lg font-bold text-gray-700">
                      Avg Deal: ${BOARD_ANALYTICS.salesHub.avgDealSize.toLocaleString()}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { onClose(); onNavigate('services'); }}
                  className="w-full mt-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors"
                >
                  View Sales Board →
                </button>
              </div>

              {/* Management Boards */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl shadow-sm border border-orange-200 p-6">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <Briefcase className="text-orange-600" size={20} />
                  Management & QA
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <div>
                      <div className="font-bold text-sm text-gray-800">Evaluation Center</div>
                      <div className="text-xs text-gray-500">QA & Sorting</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-orange-600">{BOARD_ANALYTICS.evaluation.leads}</div>
                      <div className="text-xs text-gray-400">Pending</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                    <div>
                      <div className="font-bold text-sm text-gray-800">Parking</div>
                      <div className="text-xs text-gray-500">Scheduled Leads</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-orange-600">
                        {BOARD_ANALYTICS.terminal.total}
                      </div>
                      <div className="text-xs text-gray-400">${BOARD_ANALYTICS.terminal.totalValue.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { onClose(); onNavigate('management'); }}
                  className="w-full mt-4 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition-colors"
                >
                  View Management →
                </button>
              </div>
            </div>
          )}

          {/* PHASES TAB */}
          {activeView === 'phases' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <tr>
                        <th className="text-left p-4 font-bold">Phase</th>
                        <th className="text-right p-4 font-bold">Active Leads</th>
                        <th className="text-right p-4 font-bold">Sales Value</th>
                        <th className="text-right p-4 font-bold">Deal Value</th>
                        <th className="text-right p-4 font-bold">Total Value</th>
                        <th className="text-center p-4 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {BOARD_ANALYTICS.phases.map((phase, idx) => (
                        <tr key={phase.id} className="hover:bg-blue-50 transition-colors">
                          <td className="p-4">
                            <div className="font-bold text-gray-900">{phase.name}</div>
                            <div className="text-xs text-gray-500">Construction Pipeline</div>
                          </td>
                          <td className="p-4 text-right">
                            <span className="inline-flex items-center justify-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
                              {phase.leadsActive}
                            </span>
                          </td>
                          <td className="p-4 text-right font-bold text-green-600">
                            ${phase.salesValue.toLocaleString()}
                          </td>
                          <td className="p-4 text-right font-bold text-orange-600">
                            ${phase.dealValue.toLocaleString()}
                          </td>
                          <td className="p-4 text-right font-bold text-gray-900 text-lg">
                            ${(phase.salesValue + phase.dealValue).toLocaleString()}
                          </td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">
                              <Activity size={12} /> Active
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-bold">
                        <td className="p-4 text-gray-900">TOTAL PHASES</td>
                        <td className="p-4 text-right text-blue-600">
                          {BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.leadsActive, 0)}
                        </td>
                        <td className="p-4 text-right text-green-600">
                          ${BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.salesValue, 0).toLocaleString()}
                        </td>
                        <td className="p-4 text-right text-orange-600">
                          ${BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.dealValue, 0).toLocaleString()}
                        </td>
                        <td className="p-4 text-right text-gray-900 text-xl">
                          ${BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.salesValue + p.dealValue, 0).toLocaleString()}
                        </td>
                        <td className="p-4"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* SPECIALIZED TAB */}
          {activeView === 'specialized' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-orange-600 to-pink-600 text-white">
                      <tr>
                        <th className="text-left p-4 font-bold">Board</th>
                        <th className="text-right p-4 font-bold">Active Leads</th>
                        <th className="text-right p-4 font-bold">Sales Value</th>
                        <th className="text-right p-4 font-bold">Deal Value</th>
                        <th className="text-right p-4 font-bold">Total Value</th>
                        <th className="text-center p-4 font-bold">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {BOARD_ANALYTICS.specialized.map(board => {
                        const Icon = board.icon;
                        return (
                          <tr key={board.id} className="hover:bg-orange-50 transition-colors">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="bg-orange-100 p-2 rounded-lg">
                                  <Icon size={18} className="text-orange-600" />
                                </div>
                                <div>
                                  <div className="font-bold text-gray-900">{board.name}</div>
                                  <div className="text-xs text-gray-500">Specialized Operation</div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-right">
                              <span className="inline-flex items-center justify-center bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-bold">
                                {board.leadsActive}
                              </span>
                            </td>
                            <td className="p-4 text-right font-bold text-green-600">
                              ${board.salesValue.toLocaleString()}
                            </td>
                            <td className="p-4 text-right font-bold text-orange-600">
                              ${board.dealValue.toLocaleString()}
                            </td>
                            <td className="p-4 text-right font-bold text-gray-900 text-lg">
                              ${(board.salesValue + board.dealValue).toLocaleString()}
                            </td>
                            <td className="p-4 text-center">
                              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold uppercase">
                                {board.id === 'delivery' ? 'Logistics' : board.id === 'mls' ? 'API Feed' : 'Service'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-gray-50 font-bold">
                        <td className="p-4 text-gray-900">TOTAL SPECIALIZED</td>
                        <td className="p-4 text-right text-orange-600">
                          {BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.leadsActive, 0)}
                        </td>
                        <td className="p-4 text-right text-green-600">
                          ${BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.salesValue, 0).toLocaleString()}
                        </td>
                        <td className="p-4 text-right text-orange-600">
                          ${BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.dealValue, 0).toLocaleString()}
                        </td>
                        <td className="p-4 text-right text-gray-900 text-xl">
                          ${BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.salesValue + s.dealValue, 0).toLocaleString()}
                        </td>
                        <td className="p-4"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* CHARTS TAB */}
          {activeView === 'charts' && (
            <div className="grid grid-cols-2 gap-6">
              {/* Bar Chart - Leads per Board */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-bold text-lg text-gray-800 mb-6">Leads Distribution by Board</h3>
                <div className="space-y-3">
                  {[...BOARD_ANALYTICS.phases, ...BOARD_ANALYTICS.specialized].sort((a, b) => b.leadsActive - a.leadsActive).slice(0, 10).map(board => {
                    const maxLeads = Math.max(...BOARD_ANALYTICS.phases.map(p => p.leadsActive), ...BOARD_ANALYTICS.specialized.map(s => s.leadsActive));
                    const percentage = (board.leadsActive / maxLeads) * 100;
                    return (
                      <div key={board.id}>
                        <div className="flex justify-between mb-1">
                          <span className="text-xs font-bold text-gray-700">{board.name}</span>
                          <span className="text-xs font-bold text-blue-600">{board.leadsActive}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-full rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-bold text-lg text-gray-800 mb-6">Revenue by Category</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-blue-600"></div>
                      <span className="font-bold text-gray-800">Construction Phases</span>
                    </div>
                    <span className="font-bold text-blue-600">
                      ${BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.salesValue + p.dealValue, 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-orange-600"></div>
                      <span className="font-bold text-gray-800">Specialized Boards</span>
                    </div>
                    <span className="font-bold text-orange-600">
                      ${BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.salesValue + s.dealValue, 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-green-600"></div>
                      <span className="font-bold text-gray-800">Sales & Estimates</span>
                    </div>
                    <span className="font-bold text-green-600">
                      ${BOARD_ANALYTICS.salesHub.totalValue.toLocaleString()}
                    </span>
                  </div>
                  <div className="pt-4 border-t-2 border-gray-300 flex items-center justify-between">
                    <span className="font-bold text-gray-900 text-lg">GRAND TOTAL</span>
                    <span className="font-bold text-gray-900 text-2xl">${totalSalesValue.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 col-span-2">
                <h3 className="font-bold text-lg text-gray-800 mb-6">System-Wide Lead Status</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="text-4xl font-bold text-green-600">{totalLeads - BOARD_ANALYTICS.evaluation.leads}</div>
                    <div className="text-sm font-bold text-gray-700 mt-2">Active</div>
                    <div className="text-xs text-gray-500">In Pipeline</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-xl border border-orange-200">
                    <div className="text-4xl font-bold text-orange-600">{BOARD_ANALYTICS.terminal.total}</div>
                    <div className="text-sm font-bold text-gray-700 mt-2">Parked</div>
                    <div className="text-xs text-gray-500">Scheduled by Phase</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                    <div className="text-4xl font-bold text-yellow-600">{BOARD_ANALYTICS.evaluation.leads}</div>
                    <div className="text-sm font-bold text-gray-700 mt-2">In QA</div>
                    <div className="text-xs text-gray-500">Evaluation</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="text-4xl font-bold text-blue-600">{BOARD_ANALYTICS.salesHub.active}</div>
                    <div className="text-sm font-bold text-gray-700 mt-2">In Sales</div>
                    <div className="text-xs text-gray-500">Estimating</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SALES & ESTIMATES TAB */}
          {activeView === 'sales' && (
            <div className="space-y-6">
              {/* Header Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Total Sales Boards</div>
                  <div className="text-3xl font-bold text-gray-900">{SALES_BOARDS_DATA.length}</div>
                  <div className="text-xs text-gray-500 mt-1">Active pipelines</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Total Pipeline Value</div>
                  <div className="text-3xl font-bold text-green-600">
                    ${SALES_BOARDS_DATA.reduce((acc, b) => acc + b.totalValue, 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-green-600 mt-1">All deals combined</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-4">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Closed Won</div>
                  <div className="text-3xl font-bold text-blue-600">
                    ${SALES_BOARDS_DATA.reduce((acc, b) => acc + b.closedWon, 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">Revenue secured</div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-4">
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Total Deals</div>
                  <div className="text-3xl font-bold text-orange-600">
                    {SALES_BOARDS_DATA.reduce((acc, b) => acc + b.deals.length, 0)}
                  </div>
                  <div className="text-xs text-orange-600 mt-1">Across all boards</div>
                </div>
              </div>

              {/* Sales Boards Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {SALES_BOARDS_DATA.map(board => {
                  const inProgress = board.deals.filter(d => d.stage === 'proposal' || d.stage === 'negotiation' || d.stage === 'qualified').length;
                  const won = board.deals.filter(d => d.stage === 'closed_won').length;
                  
                  return (
                    <div key={board.id} className="bg-white rounded-xl shadow-sm border-2 border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                      {/* Board Header */}
                      <div className={`p-4 bg-gradient-to-r ${
                        board.color === 'slate' ? 'from-slate-500 to-slate-600' :
                        board.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        board.color === 'indigo' ? 'from-indigo-500 to-indigo-600' :
                        board.color === 'violet' ? 'from-violet-500 to-violet-600' :
                        board.color === 'purple' ? 'from-orange-500 to-orange-600' :
                        board.color === 'emerald' ? 'from-emerald-500 to-emerald-600' :
                        board.color === 'pink' ? 'from-pink-500 to-pink-600' :
                        board.color === 'cyan' ? 'from-cyan-500 to-cyan-600' :
                        'from-gray-500 to-gray-600'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-4xl">{board.icon}</div>
                            <div>
                              <div className="text-white font-bold text-lg">{board.name}</div>
                              <div className="text-white/80 text-xs">From: {board.phase}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-white text-sm font-bold">{board.marketingLeads} Leads</div>
                            <div className="text-white/80 text-xs">from marketing</div>
                          </div>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{board.deals.length}</div>
                            <div className="text-xs text-gray-500">Total Deals</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">${(board.totalValue / 1000).toFixed(0)}K</div>
                            <div className="text-xs text-gray-500">Pipeline Value</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{board.conversionRate}%</div>
                            <div className="text-xs text-gray-500">Win Rate</div>
                          </div>
                        </div>
                      </div>

                      {/* Deal Stage Breakdown */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold text-gray-700">Pipeline Stages</h4>
                          <span className="text-xs text-gray-500">{inProgress} in progress • {won} won</span>
                        </div>
                        <div className="space-y-2">
                          {['qualified', 'proposal', 'negotiation', 'closed_won'].map(stage => {
                            const stageDeals = board.deals.filter(d => d.stage === stage);
                            const stageValue = stageDeals.reduce((acc, d) => acc + d.value, 0);
                            const stageLabel = stage === 'qualified' ? 'Qualified' :
                                              stage === 'proposal' ? 'Proposal Sent' :
                                              stage === 'negotiation' ? 'Negotiation' :
                                              'Closed Won';
                            const stageColor = stage === 'qualified' ? 'bg-yellow-100 text-yellow-700' :
                                             stage === 'proposal' ? 'bg-blue-100 text-blue-700' :
                                             stage === 'negotiation' ? 'bg-orange-100 text-orange-700' :
                                             'bg-green-100 text-green-700';
                            
                            if (stageDeals.length === 0) return null;
                            
                            return (
                              <div key={stage} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-1 rounded font-bold ${stageColor}`}>
                                    {stageLabel}
                                  </span>
                                  <span className="text-sm font-bold text-gray-700">{stageDeals.length}</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">${(stageValue / 1000).toFixed(0)}K</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Top Deals */}
                      <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Top Deals</h4>
                        <div className="space-y-2">
                          {board.deals.slice(0, 2).map(deal => (
                            <div key={deal.id} className="bg-white p-2 rounded-lg border border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-bold text-gray-800 truncate flex-1">{deal.name}</span>
                                <span className="text-sm font-bold text-green-600">${(deal.value / 1000).toFixed(0)}K</span>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>{deal.company}</span>
                                <span className="font-bold">{deal.probability}% probability</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* View Button */}
                      <div className="p-4 bg-white border-t border-gray-200">
                        <button className={`w-full py-2 rounded-lg font-bold transition-all ${
                          board.color === 'slate' ? 'bg-slate-600 hover:bg-slate-700' :
                          board.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                          board.color === 'indigo' ? 'bg-indigo-600 hover:bg-indigo-700' :
                          board.color === 'violet' ? 'bg-violet-600 hover:bg-violet-700' :
                          board.color === 'purple' ? 'bg-orange-600 hover:bg-orange-700' :
                          board.color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' :
                          board.color === 'pink' ? 'bg-pink-600 hover:bg-pink-700' :
                          board.color === 'cyan' ? 'bg-cyan-600 hover:bg-cyan-700' :
                          'bg-gray-600 hover:bg-gray-700'
                        } text-white`}>
                          View Sales Board →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MarketingCardModal = ({ card, onClose }) => {
  if (!card || card.isSales || card.isB2B) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden animate-fadeIn">
        <div className="bg-gray-50 border-b px-6 py-4 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">#{card.id}</span>
              <span className="text-xs uppercase tracking-wide font-bold text-gray-500">Marketing Lead</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{card.address}</h2>
            <p className="text-sm text-gray-500">{card.builder} • Contact: {card.contact}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-red-100 rounded text-gray-500 hover:text-red-500">
            <X size={20}/>
          </button>
        </div>
        
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-white p-4 border rounded-xl">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center">
                  <Phone size={18} className="mr-2 text-blue-600"/> Contact Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name:</span>
                    <span className="font-bold">{card.contact}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Builder:</span>
                    <span className="font-bold">{card.builder}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Action:</span>
                    <span className="font-bold">{card.lastAction}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 border border-blue-200 rounded-xl">
                <h3 className="font-bold text-blue-900 mb-3 flex items-center">
                  <MapPin size={18} className="mr-2"/> Location
                </h3>
                <p className="text-blue-800">{card.address}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-4 border rounded-xl">
                <h3 className="font-bold text-gray-800 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {(card.tags || []).map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                      {tag}
                    </span>
                  ))}
                  {(!card.tags || card.tags.length === 0) && (
                    <span className="text-gray-400 text-sm">No tags</span>
                  )}
                </div>
              </div>

              <div className="bg-white p-4 border rounded-xl">
                <h3 className="font-bold text-gray-800 mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <button className="w-full py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 flex items-center justify-center">
                    <Phone size={16} className="mr-2"/> Call Contact
                  </button>
                  <button className="w-full py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 flex items-center justify-center">
                    <CheckCircle size={16} className="mr-2"/> Move to Sales
                  </button>
                  <button className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 flex items-center justify-center">
                    <Archive size={16} className="mr-2"/> Archive
                  </button>
                </div>
              </div>
            </div>
          </div>

          {card.issue && (
            <div className="mt-6 bg-red-50 border border-red-200 p-4 rounded-xl">
              <h3 className="font-bold text-red-900 mb-2 flex items-center">
                <AlertTriangle size={18} className="mr-2"/> Issue Detected
              </h3>
              <p className="text-red-800">{card.issue}</p>
            </div>
          )}

          {card.outcome && (
            <div className="mt-6 bg-green-50 border border-green-200 p-4 rounded-xl">
              <h3 className="font-bold text-green-900 mb-2 flex items-center">
                <CheckCircle size={18} className="mr-2"/> Outcome
              </h3>
              <p className="text-green-800">{card.outcome}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SalesCardModal = ({ card, onClose, onOpenTakeoff }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [comments, setComments] = useState([
    { id: 1, user: 'Marketing Team', text: card.telemarketerNote, date: '2 hours ago', role: 'internal' },
  ]);
  const [newComment, setNewComment] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  
  if (!card || !card.isSales) return null;
  
  // Available team members to tag
  const teamMembers = [
    { id: 'sarah', name: 'Sarah Jenkins', role: 'Telemarketer' },
    { id: 'david', name: 'David Ross', role: 'Telemarketer' },
    { id: 'tom', name: 'Tom Digital', role: 'Digital Marketer' },
    { id: 'sales', name: 'Sales Team', role: 'Sales' },
    { id: 'management', name: 'Management', role: 'Management' },
  ];

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map((file, idx) => ({
      id: Date.now() + idx,
      name: file.name,
      url: URL.createObjectURL(file),
      date: new Date().toISOString()
    }));
    setUploadedPhotos([...uploadedPhotos, ...newPhotos]);
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const commentText = selectedTag 
      ? `@${teamMembers.find(m => m.id === selectedTag)?.name}: ${newComment}`
      : newComment;
    
    setComments([...comments, {
      id: Date.now(),
      user: 'You (Third Party)',
      text: commentText,
      date: 'Just now',
      role: 'third_party',
      taggedUser: selectedTag
    }]);
    setNewComment('');
    setSelectedTag('');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'documents', label: 'Documents', icon: Folder },
    { id: 'takeoff', label: 'Takeoff & Estimate', icon: Calculator },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Receipt size={16} />
                <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded">JOB #{card.id}</span>
                <span className={`text-xs px-2 py-1 rounded font-bold ${
                  card.status === 'estimating' ? 'bg-orange-500' :
                  card.status === 'quote_sent' ? 'bg-yellow-500' :
                  card.status === 'deal_won' ? 'bg-green-500' :
                  'bg-blue-500'
                }`}>
                  {card.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <h2 className="text-2xl font-bold mb-1">{card.address}</h2>
              <div className="flex items-center gap-4 text-sm text-slate-300">
                <span className="flex items-center gap-1">
                  <Building size={14} />
                  {card.trade}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  Due: {card.deadline}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors">
              <X size={24}/>
            </button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-slate-100 border-b border-slate-200 px-6">
          <div className="flex gap-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-bold text-sm transition-colors relative ${
                    activeTab === tab.id
                      ? 'text-indigo-600 bg-white'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Project Information */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin size={18} className="text-slate-600" />
                    Project Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Location</span>
                      <span className="text-sm font-bold text-slate-900">{card.address}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Trade</span>
                      <span className="text-sm font-bold text-slate-900">{card.trade}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Source</span>
                      <span className="text-sm font-bold text-slate-900">{card.source}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Deadline</span>
                      <span className="text-sm font-bold text-orange-600">{card.deadline}</span>
                    </div>
                    {card.jobsiteVisit && (
                      <div className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <CalendarDays size={14} className="text-blue-500" />
                          Jobsite Visit
                        </span>
                        <span className="text-sm font-bold text-blue-600">{card.jobsiteVisit}</span>
                      </div>
                    )}
                    {card.negotiation && (
                      <div className="flex justify-between py-2">
                        <span className="text-sm text-slate-600 flex items-center gap-1">
                          <HeartHandshake size={14} className="text-orange-500" />
                          Negotiation
                        </span>
                        <span className="text-sm font-bold text-orange-600">{card.negotiation}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('takeoff')}
                      className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Calculator size={18} />
                      Open Takeoff Module
                    </button>
                    <button
                      onClick={() => setActiveTab('documents')}
                      className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Folder size={18} />
                      View Documents
                    </button>
                    <button
                      onClick={() => setActiveTab('comments')}
                      className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                      <MessageSquare size={18} />
                      Add Comment
                    </button>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-amber-900 mb-2">Instructions from Marketing Team:</div>
                    <p className="text-sm text-amber-800">{card.telemarketerNote}</p>
                  </div>
                </div>
              </div>

              {/* Submit Quote */}
              {card.status !== 'quote_sent' && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">Submit Your Quotation</h3>
                  <p className="text-sm text-gray-600 mb-4">Once complete, submit your quote to the internal team</p>
                  <button
                    onClick={() => { 
                      alert("Quotation sent successfully!"); 
                      onClose(); 
                    }}
                    className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 shadow-lg transition-all"
                  >
                    <Send size={18} />
                    Send Quotation
                  </button>
                </div>
              )}
            </div>
          )}

          {/* DOCUMENTS TAB */}
          {activeTab === 'documents' && (
            <div className="max-w-5xl mx-auto space-y-6">
              {/* Jobsite Photos */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Camera size={20} className="text-slate-600" />
                    <h3 className="font-bold text-lg text-gray-900">Jobsite Photos</h3>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full font-bold">
                    {uploadedPhotos.length} uploaded
                  </span>
                </div>
                
                {/* Photo Upload Area */}
                <div className="mb-6">
                  <label className="block">
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-indigo-400 hover:bg-indigo-50 cursor-pointer transition-all">
                      <UploadCloud size={48} className="mx-auto text-slate-400 mb-3" />
                      <p className="text-base text-slate-600 font-medium mb-1">Click to upload jobsite photos</p>
                      <p className="text-sm text-slate-500">or drag and drop • PNG, JPG up to 10MB</p>
                    </div>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*" 
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Uploaded Photos Grid */}
                {uploadedPhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-4">
                    {uploadedPhotos.map(photo => (
                      <div key={photo.id} className="relative group">
                        <img 
                          src={photo.url} 
                          alt={photo.name}
                          className="w-full h-32 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          onClick={() => setUploadedPhotos(uploadedPhotos.filter(p => p.id !== photo.id))}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                        >
                          <X size={14} />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-lg">
                          {photo.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Plans & Documents */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText size={20} className="text-slate-600" />
                  <h3 className="font-bold text-lg text-gray-900">Plans & Specifications</h3>
                </div>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded">
                        <FileText size={20} className="text-blue-600" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-sm">Site Plans.pdf</div>
                        <div className="text-xs text-slate-500">2.4 MB • Uploaded by Marketing</div>
                      </div>
                    </div>
                    <Download size={20} className="text-slate-400" />
                  </button>
                  <button className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded">
                        <FileText size={20} className="text-blue-600" />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-sm">Project Specifications.pdf</div>
                        <div className="text-xs text-slate-500">1.8 MB • Uploaded by Marketing</div>
                      </div>
                    </div>
                    <Download size={20} className="text-slate-400" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAKEOFF & ESTIMATE TAB */}
          {activeTab === 'takeoff' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-indigo-50 to-orange-50 border-2 border-indigo-300 rounded-xl p-8 text-center">
                <div className="bg-indigo-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calculator size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Material Takeoff & Estimation</h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Launch the comprehensive estimation workspace to calculate materials, labor costs, and generate accurate quotations for this project.
                </p>
                <button 
                  onClick={() => {
                    onClose();
                    if (onOpenTakeoff) onOpenTakeoff(card);
                  }}
                  className="inline-flex items-center gap-2 py-4 px-8 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-lg shadow-lg transition-all hover:scale-105"
                >
                  <Ruler size={24} />
                  Launch Takeoff Module
                </button>
              </div>

              {/* Additional Tools */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                  <Calculator size={32} className="mx-auto text-indigo-600 mb-2" />
                  <div className="font-bold text-sm">Material Calculator</div>
                  <div className="text-xs text-slate-500 mt-1">Calculate quantities</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                  <DollarSign size={32} className="mx-auto text-green-600 mb-2" />
                  <div className="font-bold text-sm">Labor Estimator</div>
                  <div className="text-xs text-slate-500 mt-1">Calculate labor costs</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
                  <Receipt size={32} className="mx-auto text-blue-600 mb-2" />
                  <div className="font-bold text-sm">Quote Generator</div>
                  <div className="text-xs text-slate-500 mt-1">Generate final quote</div>
                </div>
              </div>
            </div>
          )}

          {/* COMMENTS TAB */}
          {activeTab === 'comments' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                  <MessageSquare size={20} className="text-slate-600" />
                  Project Comments
                </h3>

                {/* Comments List */}
                <div className="space-y-4 mb-6">
                  {comments.map(comment => (
                    <div 
                      key={comment.id}
                      className={`p-4 rounded-lg border ${
                        comment.role === 'system'
                          ? 'bg-green-50 border-green-200'
                          : comment.role === 'internal' 
                            ? 'bg-amber-50 border-amber-200' 
                            : comment.taggedUser 
                              ? 'bg-orange-50 border-orange-300 border-2'
                              : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          {comment.role === 'system' && (
                            <CalendarDays size={14} className="text-green-600" />
                          )}
                          {comment.taggedUser && (
                            <Users size={14} className="text-orange-600" />
                          )}
                          <div className="font-bold text-sm">{comment.user}</div>
                          {comment.taggedUser && (
                            <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-medium">
                              Tagged: {teamMembers.find(m => m.id === comment.taggedUser)?.name}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{comment.date}</div>
                      </div>
                      <p className="text-sm text-slate-700">{comment.text}</p>
                    </div>
                  ))}
                </div>

                {/* Add Comment */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-bold text-slate-700 mb-2">Add Your Comment</label>
                  
                  {/* Tag Team Member */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-2">Tag Team Member (Optional)</label>
                    <select
                      value={selectedTag}
                      onChange={(e) => setSelectedTag(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    >
                      <option value="">-- No Tag --</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.name} ({member.role})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Tag a team member to notify them about issues like missing plans, questions, or important updates.
                    </p>
                  </div>
                  
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Example: Plans are incomplete - missing electrical layout. Please provide updated drawings."
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                  
                  {selectedTag && (
                    <div className="mt-2 text-xs bg-indigo-50 border border-indigo-200 rounded-lg p-2 flex items-center gap-2">
                      <Users size={14} className="text-indigo-600" />
                      <span className="text-indigo-700">
                        Will notify: <strong>{teamMembers.find(m => m.id === selectedTag)?.name}</strong>
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      className="py-2 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2 transition-colors"
                    >
                      <Send size={16} />
                      Post Comment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sales Follow-Up Modal (for quote_sent, follow_up, deal_won, lost statuses)
const SalesFollowUpModal = ({ card, onClose }) => {
  const [comments, setComments] = useState([
    { id: 1, user: card.telemarketerName || 'Marketing Team', text: card.telemarketerNote, date: '2 hours ago', role: 'internal' },
  ]);
  const [newComment, setNewComment] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [isEditingEstimate, setIsEditingEstimate] = useState(false);
  
  if (!card || !card.isSales) return null;
  
  // Available team members to tag
  const teamMembers = [
    { id: 'sarah', name: 'Sarah Jenkins', role: 'Telemarketer' },
    { id: 'david', name: 'David Ross', role: 'Telemarketer' },
    { id: 'tom', name: 'Tom Digital', role: 'Digital Marketer' },
    { id: 'sales', name: 'Sales Team', role: 'Sales' },
    { id: 'management', name: 'Management', role: 'Management' },
  ];

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const commentText = selectedTag 
      ? `@${teamMembers.find(m => m.id === selectedTag)?.name}: ${newComment}`
      : newComment;
    
    setComments([...comments, {
      id: Date.now(),
      user: 'Sales Team',
      text: commentText,
      date: 'Just now',
      role: 'internal',
      taggedUser: selectedTag
    }]);
    setNewComment('');
    setSelectedTag('');
  };

  const mockEstimate = {
    materials: '$8,500',
    labor: '$4,200',
    equipment: '$1,800',
    overhead: '$1,200',
    profit: '$1,800',
    total: '$17,500',
    submittedDate: 'Feb 16, 2026 3:30 PM',
    submittedBy: 'Smith Electrical Services'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-6 py-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Receipt size={16} />
                <span className="text-xs font-bold bg-white/20 px-2 py-1 rounded">JOB #{card.id}</span>
                <span className={`text-xs px-2 py-1 rounded font-bold ${
                  card.status === 'quote_sent' ? 'bg-yellow-500' :
                  card.status === 'follow_up' ? 'bg-orange-500' :
                  card.status === 'deal_won' ? 'bg-green-500' :
                  'bg-red-500'
                }`}>
                  {card.status === 'deal_lost' ? 'LOST' : card.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <h2 className="text-2xl font-bold mb-1">{card.address}</h2>
              <div className="flex items-center gap-4 text-sm text-slate-300">
                <span className="flex items-center gap-1">
                  <Building size={14} />
                  {card.type || card.trade}
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign size={14} />
                  Est: {mockEstimate.total}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors">
              <X size={24}/>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Communication Tools */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-4">Communication</h3>
              <div className="flex gap-3">
                <button className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
                  <Phone size={18} />
                  Call {card.contact}
                </button>
                <button className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
                  <MessageSquare size={18} />
                  Send SMS
                </button>
                <button className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
                  <Mail size={18} />
                  Email
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Project & Estimate Information */}
              <div className="space-y-6">
                {/* Project Details */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin size={18} className="text-slate-600" />
                    Project Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Location</span>
                      <span className="text-sm font-bold text-slate-900">{card.address}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Trade</span>
                      <span className="text-sm font-bold text-slate-900">{card.type || card.trade}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Contact</span>
                      <span className="text-sm font-bold text-slate-900">{card.contact}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-600">Source</span>
                      <span className="text-sm font-bold text-slate-900">{card.source}</span>
                    </div>
                    {card.deadline && (
                      <div className="flex justify-between py-2">
                        <span className="text-sm text-slate-600">Deadline</span>
                        <span className="text-sm font-bold text-orange-600">{card.deadline}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Estimate Breakdown */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border-2 border-green-300 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                      <Receipt size={18} className="text-green-600" />
                      Submitted Estimate
                    </h3>
                    {card.status !== 'deal_won' && card.status !== 'deal_lost' && (
                      <button 
                        onClick={() => setIsEditingEstimate(!isEditingEstimate)}
                        className="text-xs bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 rounded-lg font-medium border border-gray-300 flex items-center gap-1"
                      >
                        <Edit2 size={12} />
                        Edit
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Materials</span>
                      <span className="font-bold text-gray-900">{mockEstimate.materials}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Labor</span>
                      <span className="font-bold text-gray-900">{mockEstimate.labor}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Equipment</span>
                      <span className="font-bold text-gray-900">{mockEstimate.equipment}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Overhead & Admin</span>
                      <span className="font-bold text-gray-900">{mockEstimate.overhead}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Profit Margin</span>
                      <span className="font-bold text-gray-900">{mockEstimate.profit}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t-2 border-green-400">
                      <span className="font-bold text-gray-900">Total Estimate</span>
                      <span className="font-bold text-green-700 text-lg">{mockEstimate.total}</span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-600 mb-3">
                    <div>Submitted by: <strong>{mockEstimate.submittedBy}</strong></div>
                    <div>Date: {mockEstimate.submittedDate}</div>
                  </div>

                  {card.status !== 'deal_won' && card.status !== 'deal_lost' && (
                    <button className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center justify-center gap-2 transition-colors">
                      <Send size={16} />
                      Resend Quote to Client
                    </button>
                  )}
                </div>
              </div>

              {/* Comments & Activity */}
              <div className="space-y-6">
                {/* AI Suggestions */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <Zap size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-bold text-blue-900 mb-2">AI Suggestion:</div>
                      <p className="text-sm text-blue-800 mb-3">
                        {card.status === 'quote_sent' ? 'Follow up within 24-48 hours. Best time to call: 10 AM - 12 PM.' :
                         card.status === 'follow_up' ? 'Client showed interest. Consider offering a 5% discount for quick decision.' :
                         card.status === 'deal_won' ? 'Schedule kickoff meeting and send contract documents.' :
                         card.status === 'deal_lost' ? 'Analyze lost reason and update CRM notes for future reference.' :
                         'Update status and add notes.'}
                      </p>
                      {card.status === 'follow_up' && (
                        <button className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded font-medium">
                          Apply Suggestion
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Comments */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare size={20} className="text-slate-600" />
                    Comments & Notes
                  </h3>

                  {/* Comments List */}
                  <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                    {comments.map(comment => (
                      <div 
                        key={comment.id}
                        className={`p-3 rounded-lg border text-sm ${
                          comment.role === 'internal' 
                            ? 'bg-amber-50 border-amber-200' 
                            : comment.taggedUser 
                              ? 'bg-orange-50 border-orange-300 border-2'
                              : 'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center gap-2">
                            {comment.taggedUser && (
                              <Users size={12} className="text-orange-600" />
                            )}
                            <div className="font-bold text-xs">{comment.user}</div>
                            {comment.taggedUser && (
                              <span className="text-xs bg-orange-200 text-orange-800 px-1.5 py-0.5 rounded-full font-medium">
                                @{teamMembers.find(m => m.id === comment.taggedUser)?.name}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{comment.date}</div>
                        </div>
                        <p className="text-xs text-slate-700">{comment.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* Add Comment */}
                  <div className="border-t pt-4">
                    <select
                      value={selectedTag}
                      onChange={(e) => setSelectedTag(e.target.value)}
                      className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 mb-2 bg-white"
                    >
                      <option value="">-- Tag Team Member (Optional) --</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.name} ({member.role})
                        </option>
                      ))}
                    </select>
                    
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add follow-up notes, client feedback, or next steps..."
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 resize-none mb-2"
                      rows={3}
                    />
                    
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors"
                    >
                      <Send size={14} />
                      Add Note
                    </button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-sm text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full py-2 text-left px-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm flex items-center gap-2 transition-colors">
                      <CalendarDays size={14} className="text-slate-600" />
                      Schedule Follow-up
                    </button>
                    <button className="w-full py-2 text-left px-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm flex items-center gap-2 transition-colors">
                      <FileText size={14} className="text-slate-600" />
                      Generate Contract
                    </button>
                    <button className="w-full py-2 text-left px-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm flex items-center gap-2 transition-colors">
                      <TrendingUp size={14} className="text-slate-600" />
                      Update Status
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Next Move Options */}
        <div className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-slate-700">Next Move:</span>
              <div className="flex gap-2">
                {/* Show options based on current status */}
                {card.status === 'quote_sent' && (
                  <>
                    <button 
                      onClick={() => alert('Moving to Follow Up')}
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Move to Follow Up
                    </button>
                    <button 
                      onClick={() => alert('Deal Won!')}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Won Deal
                    </button>
                    <button 
                      onClick={() => alert('Deal Lost')}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Lost
                    </button>
                  </>
                )}
                {card.status === 'follow_up' && (
                  <>
                    <button 
                      onClick={() => alert('Deal Won!')}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Won Deal
                    </button>
                    <button 
                      onClick={() => alert('Deal Lost')}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Lost
                    </button>
                    <button 
                      onClick={() => alert('Moving back to Quote Sent')}
                      className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-medium transition-colors text-sm"
                    >
                      Back to Quote Sent
                    </button>
                  </>
                )}
                {(card.status === 'deal_won' || card.status === 'deal_lost') && (
                  <div className="text-sm text-slate-500 italic">
                    {card.status === 'deal_won' ? 'Deal completed - Move to Services for project management' : 'Deal closed - Update notes for future reference'}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const B2BLeadModal = ({ lead, onClose, onConfigure }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!lead || !lead.isB2B) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden animate-fadeIn">
        <div className="bg-slate-50 border-b px-6 py-4 flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-slate-800 text-white text-xs font-bold px-2 py-0.5 rounded">B2B PROSPECT</span>
              <span className="text-slate-500 text-xs uppercase tracking-wide font-bold">{lead.status.replace('_', ' ')}</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900">{lead.name}</h2>
            <p className="text-sm text-slate-500">{lead.type} • {lead.contact}</p>
          </div>
          <div className="flex gap-2">
            {lead.status === 'config_pending' && (
              <button
                onClick={() => onConfigure(lead)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold shadow-md hover:bg-green-700 flex items-center gap-2 animate-pulse"
              >
                <SettingsIcon size={16}/> Start Configuration
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded text-slate-500">
              <X size={20}/>
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-16 bg-slate-50 border-r border-slate-200 flex flex-col items-center py-4 gap-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`p-3 rounded-xl transition-all ${activeTab === 'overview' ? 'bg-white shadow text-blue-600' : 'text-slate-400 hover:bg-white'}`}
              title="Overview"
            >
              <LayoutDashboard size={20}/>
            </button>
            <button
              onClick={() => setActiveTab('agreement')}
              className={`p-3 rounded-xl transition-all ${activeTab === 'agreement' ? 'bg-white shadow text-blue-600' : 'text-slate-400 hover:bg-white'}`}
              title="Agreement"
            >
              <FileText size={20}/>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`p-3 rounded-xl transition-all ${activeTab === 'history' ? 'bg-white shadow text-blue-600' : 'text-slate-400 hover:bg-white'}`}
              title="History"
            >
              <Activity size={20}/>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-8 bg-white">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <DollarSign size={18} className="text-green-600"/> Deal Value
                    </h3>
                    <div className="text-3xl font-bold text-slate-900 mb-1">{lead.value}</div>
                    <p className="text-xs text-slate-500">Estimated Monthly Recurring Revenue</p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold text-slate-800 text-sm uppercase">Quick Actions</h3>
                    <button className="w-full text-left px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center">
                      <Phone size={16} className="mr-3 text-slate-400"/> Log Call
                    </button>
                    <button className="w-full text-left px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center">
                      <Mail size={16} className="mr-3 text-slate-400"/> Send Email
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-slate-800 mb-2">Notes</h3>
                    <textarea
                      className="w-full h-32 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      defaultValue={lead.note}
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                    <h4 className="font-bold text-blue-900 mb-2 text-sm flex items-center">
                      <Activity size={14} className="mr-2"/> Status
                    </h4>
                    <p className="text-xs text-blue-800">
                      This lead is in the <span className="font-bold">{lead.status.replace('_', ' ')}</span> stage.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'agreement' && (
              <div className="space-y-6">
                <h3 className="font-bold text-slate-800 text-lg">Contracts & Proposals</h3>
                <div className="text-center py-10 text-slate-400 text-sm">
                  No agreements uploaded yet.
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="relative pl-6 border-l-2 border-slate-200 space-y-8">
                <div className="relative">
                  <div className="absolute -left-[31px] top-0 w-4 h-4 rounded-full bg-blue-600 border-4 border-white shadow-sm"></div>
                  <h4 className="font-bold text-slate-800 text-sm">Lead Created</h4>
                  <p className="text-xs text-slate-500">Added to pipeline • 5 days ago</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientConfigurationModal = ({ client, onClose, onComplete }) => {
  if (!client) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex overflow-hidden">
        <div className="w-64 bg-slate-50 border-r p-6">
          <h2 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
            <SettingsIcon size={20}/> Setup Client
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-blue-600 font-bold">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs">1</div>
              Business Data
            </div>
            <div className="flex items-center gap-3 text-slate-400">
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">2</div>
              Insurance & Legal
            </div>
            <div className="flex items-center gap-3 text-slate-400">
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">3</div>
              Comms Setup
            </div>
          </div>
        </div>

        <div className="flex-1 p-8 overflow-y-auto">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Configuring: {client.name}</h1>
          <p className="text-slate-500 mb-8">Set up the service environment for this B2B partner.</p>

          <div className="grid grid-cols-2 gap-6 mb-8">
            <div className="p-4 border rounded-xl hover:border-blue-300 cursor-pointer">
              <div className="bg-orange-100 w-10 h-10 rounded-lg flex items-center justify-center mb-3 text-orange-600">
                <UploadCloud size={20}/>
              </div>
              <h3 className="font-bold">Upload Branding</h3>
              <p className="text-xs text-slate-500">Logo, Letterhead, Colors</p>
            </div>
            <div className="p-4 border rounded-xl hover:border-blue-300 cursor-pointer">
              <div className="bg-green-100 w-10 h-10 rounded-lg flex items-center justify-center mb-3 text-green-600">
                <Shield size={20}/>
              </div>
              <h3 className="font-bold">Verify Insurance</h3>
              <p className="text-xs text-slate-500">WSIB, Liability Upload</p>
            </div>
            <div className="p-4 border rounded-xl hover:border-blue-300 cursor-pointer">
              <div className="bg-blue-100 w-10 h-10 rounded-lg flex items-center justify-center mb-3 text-blue-600">
                <Phone size={20}/>
              </div>
              <h3 className="font-bold">Provision VoIP</h3>
              <p className="text-xs text-slate-500">Generate Phone Number</p>
            </div>
            <div className="p-4 border rounded-xl hover:border-blue-300 cursor-pointer">
              <div className="bg-orange-100 w-10 h-10 rounded-lg flex items-center justify-center mb-3 text-orange-600">
                <Mail size={20}/>
              </div>
              <h3 className="font-bold">Email DNS</h3>
              <p className="text-xs text-slate-500">DKIM/SPF Setup</p>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button onClick={onClose} className="px-6 py-3 text-slate-500 font-bold hover:bg-slate-100 rounded-lg">
              Cancel
            </button>
            <button
              onClick={onComplete}
              className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2"
            >
              <CheckCircle size={18}/> Complete Setup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SIDEBAR COMPONENT ====================

const SidebarItem = ({ item, isActive, onClick, isExpanded, onToggleExpand, onSubItemClick }) => {
  const Icon = item.icon;
  return (
    <div className="mb-1">
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between p-3 text-sm transition-colors duration-200 border-l-4 ${
          isActive
            ? 'bg-blue-50 border-blue-600 text-blue-900'
            : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        <div className="flex items-center">
          <Icon size={18} className={`mr-3 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
          <div className="text-left">
            <div className="font-medium">{item.label}</div>
            {item.subLabel && <div className="text-xs text-gray-400">{item.subLabel}</div>}
          </div>
        </div>
        {item.boards && item.boards.length > 0 && (
          <div onClick={(e) => { e.stopPropagation(); onToggleExpand(); }} className="p-1 hover:bg-gray-200 rounded">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </div>
        )}
      </button>
      {item.boards && isExpanded && item.boards.length > 0 && (
        <div className="bg-gray-50 py-1">
          {item.boards.map(board => (
            <div
              key={board}
              onClick={(e) => { e.stopPropagation(); onSubItemClick(board); }}
              className="pl-12 py-2 text-xs text-gray-500 hover:text-blue-600 cursor-pointer font-medium hover:bg-gray-100 transition-colors"
            >
              {board}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== DASHBOARD VIEW ====================

const DashboardView = ({ onNavigate, showLeadsAnalytics, setShowLeadsAnalytics }) => {
  
  console.log('🟢 DashboardView render, showLeadsAnalytics:', showLeadsAnalytics);

  console.log('🟣 RENDER START - showLeadsAnalytics:', showLeadsAnalytics);
  
  return (
    <div className="p-8 h-full overflow-y-auto bg-slate-50">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Executive Dashboard</h2>
          <p className="text-slate-500">Real-time overview of operations, sales, and logistics.</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-slate-400 uppercase tracking-wider">System Status</div>
          <div className="flex items-center justify-end gap-2 text-green-600 font-bold">
            <div className="w-2 h-2 rounded-full bg-green-600 animate-pulse"></div>
            Online & Healthy
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('🔵 Total Leads clicked!');
            setShowLeadsAnalytics(true);
            console.log('🔵 State set to true');
          }}
          className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between hover:shadow-xl hover:border-blue-400 transition-all group cursor-pointer text-left"
        >
          <div className="flex justify-between items-start">
            <div className="bg-blue-50 p-3 rounded-xl text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
              <Users size={22} />
            </div>
            <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-0.5 rounded flex items-center">
              <TrendingUp size={12} className="mr-1" /> +12%
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-slate-900">
              {BOARD_ANALYTICS.phases.reduce((acc, p) => acc + p.leadsActive, 0) + 
               BOARD_ANALYTICS.specialized.reduce((acc, s) => acc + s.leadsActive, 0)}
            </div>
            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Total Leads</div>
            <div className="text-xs text-blue-600 font-bold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              Click for detailed analytics →
            </div>
          </div>
        </button>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between hover:shadow-md transition-all group">
          <div className="flex justify-between items-start">
            <div className="bg-green-50 p-3 rounded-xl text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors">
              <DollarSign size={22} />
            </div>
            <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-0.5 rounded flex items-center">
              <TrendingUp size={12} className="mr-1" /> +5%
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-slate-900">$45,200</div>
            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Revenue MTD</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between hover:shadow-md transition-all group">
          <div className="flex justify-between items-start">
            <div className="bg-orange-50 p-3 rounded-xl text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors">
              <Briefcase size={22} />
            </div>
            <span className="text-slate-400 text-xs font-bold bg-slate-50 px-2 py-0.5 rounded">Active</span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-slate-900">18</div>
            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Active Jobs</div>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between hover:shadow-md transition-all group">
          <div className="flex justify-between items-start">
            <div className="bg-orange-50 p-3 rounded-xl text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors">
              <AlertTriangle size={22} />
            </div>
            <span className="text-red-600 text-xs font-bold bg-red-50 px-2 py-0.5 rounded animate-pulse">Action Req</span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-slate-900">5</div>
            <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">Pending Alerts</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Access */}
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h3 className="text-lg font-bold text-slate-800 mb-4">Quick Access</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button onClick={() => onNavigate('data_collection')} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-3 group py-6">
                <div className="bg-blue-50 p-3 rounded-full text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shadow-sm">
                  <Plus size={24} />
                </div>
                <span className="text-sm font-bold text-slate-700">Add Lead</span>
              </button>
              <button onClick={() => onNavigate('sales_pr')} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-orange-400 hover:shadow-md transition-all flex flex-col items-center gap-3 group py-6">
                <div className="bg-orange-50 p-3 rounded-full text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors shadow-sm">
                  <Megaphone size={24} />
                </div>
                <span className="text-sm font-bold text-slate-700">B2B Prospect</span>
              </button>
              <button onClick={() => onNavigate('services')} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-orange-400 hover:shadow-md transition-all flex flex-col items-center gap-3 group py-6">
                <div className="bg-orange-50 p-3 rounded-full text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-colors shadow-sm">
                  <Truck size={24} />
                </div>
                <span className="text-sm font-bold text-slate-700">Dispatch</span>
              </button>
              <button onClick={() => onNavigate('users_files')} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-green-400 hover:shadow-md transition-all flex flex-col items-center gap-3 group py-6">
                <div className="bg-green-50 p-3 rounded-full text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors shadow-sm">
                  <Users size={24} />
                </div>
                <span className="text-sm font-bold text-slate-700">Staff Center</span>
              </button>
            </div>
          </section>

          {/* Active Operations */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <MapIcon size={20} className="text-slate-400" /> Live Operations
              </h3>
              <button onClick={() => onNavigate('services')} className="text-blue-600 text-xs font-bold hover:underline bg-blue-50 px-3 py-1 rounded-full">
                View All Boards
              </button>
            </div>
            <div className="p-2">
              <div className="grid grid-cols-1 divide-y divide-slate-100">
                <div className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold shadow-sm">P2</div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm group-hover:text-blue-600 transition-colors">14 Bayview Ridge</div>
                      <div className="text-xs text-slate-500">Framing • Contractor: GreenTime</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-[10px] font-bold border border-green-100">On Schedule</span>
                    <div className="text-[10px] text-slate-400 mt-1">Updated 2h ago</div>
                  </div>
                </div>
                <div className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center font-bold shadow-sm">P3</div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm group-hover:text-blue-600 transition-colors">88 York Mills</div>
                      <div className="text-xs text-slate-500">MEC/Drywall • Contractor: Elite</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-[10px] font-bold border border-yellow-100">Inspection Pending</span>
                    <div className="text-[10px] text-slate-400 mt-1">Updated 5h ago</div>
                  </div>
                </div>
                <div className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-100 text-cyan-600 flex items-center justify-center font-bold shadow-sm">
                      <Snowflake size={18} />
                    </div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm group-hover:text-blue-600 transition-colors">Propane Delivery #509</div>
                      <div className="text-xs text-slate-500">Logistics • Driver: Majid</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-[10px] font-bold border border-blue-100">En Route</span>
                    <div className="text-[10px] text-slate-400 mt-1">Updated 10m ago</div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* System Health & Activity */}
        <div className="space-y-8">
          <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-lg relative overflow-hidden group">
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold flex items-center gap-2">
                  <Activity size={18} /> System Health
                </h3>
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse shadow-[0_0_10px_rgba(74,222,128,0.5)]"></div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between text-sm items-center border-b border-white/10 pb-2">
                  <span className="text-slate-400">API Latency</span>
                  <span className="text-green-400 font-mono font-bold">24ms</span>
                </div>
                <div className="flex justify-between text-sm items-center border-b border-white/10 pb-2">
                  <span className="text-slate-400">Active AI Agents</span>
                  <span className="text-blue-300 font-mono font-bold">12/15</span>
                </div>
                <div className="flex justify-between text-sm items-center border-b border-white/10 pb-2">
                  <span className="text-slate-400">Database Load</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 w-[35%]"></div>
                    </div>
                    <span className="text-green-400 font-mono font-bold">35%</span>
                  </div>
                </div>
                <div className="flex justify-between text-sm items-center pt-2">
                  <span className="text-slate-400">Cloud Storage</span>
                  <span className="text-slate-200 font-mono text-xs">450GB / 1TB</span>
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600 rounded-full blur-[80px] opacity-20 -mr-10 -mt-10 group-hover:opacity-30 transition-opacity duration-700"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-orange-600 rounded-full blur-[60px] opacity-20 -ml-10 -mb-10 group-hover:opacity-30 transition-opacity duration-700"></div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-6 flex items-center justify-between">
              Recent Activity
              <button className="text-slate-400 hover:text-slate-600">
                <MoreVertical size={16} />
              </button>
            </h3>
            <div className="space-y-6 relative before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
              <div className="relative pl-8">
                <div className="absolute left-0 top-1 w-5 h-5 bg-blue-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
                <p className="text-sm text-slate-800 leading-snug">
                  <span className="font-bold text-blue-700">Sarah Jenkins</span> moved <span className="font-bold text-slate-900">14 Bayview</span> to Closing.
                </p>
                <p className="text-xs text-slate-400 mt-1">10 minutes ago • Sales Board</p>
              </div>
              <div className="relative pl-8">
                <div className="absolute left-0 top-1 w-5 h-5 bg-green-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                </div>
                <p className="text-sm text-slate-800 leading-snug">
                  Payment of <span className="font-bold text-green-700">$2,500</span> received from <span className="font-bold text-slate-900">Mega Windows</span>.
                </p>
                <p className="text-xs text-slate-400 mt-1">45 minutes ago • Finance</p>
              </div>
              <div className="relative pl-8">
                <div className="absolute left-0 top-1 w-5 h-5 bg-orange-100 rounded-full border-4 border-white shadow-sm flex items-center justify-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                </div>
                <p className="text-sm text-slate-800 leading-snug">
                  System Alert: High "Backed" rate detected in <span className="font-bold text-slate-900">Data Collection</span>.
                </p>
                <p className="text-xs text-slate-400 mt-1">2 hours ago • AI Monitor</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== DATA COLLECTION VIEW ====================

const CollectorHeatmap = () => {
  // Mock active collector locations
  const collectors = [
    { id: 1, name: 'Majid Survey', lat: 43.7, lng: -79.4, status: 'active', route: 'York Mills West' },
    { id: 2, name: 'Ken Delivery', lat: 43.72, lng: -79.38, status: 'active', route: 'Bayview Install' }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden h-full">
      <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
        <div>
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <MapIcon size={20} className="text-blue-600" />
            Live Collector Heatmap
          </h3>
          <p className="text-xs text-gray-500 mt-1">Real-time field team location tracking</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded-full">
            <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
            <span className="text-xs font-bold text-green-700">{collectors.length} Active</span>
          </div>
        </div>
      </div>

      {/* Map Visualization */}
      <div className="flex-1 relative bg-gradient-to-br from-slate-100 to-blue-50">
        {/* Simulated Map Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(0deg, #ccc 0px, #ccc 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, #ccc 0px, #ccc 1px, transparent 1px, transparent 40px)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Collector Markers */}
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="relative w-full h-full max-w-4xl">
            {collectors.map((collector, idx) => (
              <div
                key={collector.id}
                className="absolute animate-pulse"
                style={{
                  left: `${30 + (idx * 35)}%`,
                  top: `${40 + (idx * 15)}%`
                }}
              >
                {/* Pulsing Circle */}
                <div className="relative">
                  <div className="absolute -inset-4 bg-blue-400 rounded-full opacity-30 animate-ping"></div>
                  <div className="relative w-12 h-12 bg-blue-600 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                    <Navigation size={20} className="text-white" />
                  </div>
                </div>
                
                {/* Info Card */}
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-xl p-3 min-w-[180px] border-2 border-blue-500">
                  <div className="font-bold text-sm text-gray-900">{collector.name}</div>
                  <div className="text-xs text-gray-500 mt-1">Route: {collector.route}</div>
                  <div className="flex items-center gap-1 mt-2">
                    <Activity size={12} className="text-green-600" />
                    <span className="text-xs font-bold text-green-600">Live Tracking</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map Legend */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
          <h4 className="font-bold text-xs text-gray-700 mb-3">Legend</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <span className="text-xs text-gray-600">Active Collector</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-600 rounded-full"></div>
              <span className="text-xs text-gray-600">Completed Task</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-amber-600 rounded-full"></div>
              <span className="text-xs text-gray-600">En Route</span>
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
          <h4 className="font-bold text-xs text-gray-700 mb-3">Today's Activity</h4>
          <div className="space-y-2">
            <div className="flex justify-between gap-8">
              <span className="text-xs text-gray-500">Distance</span>
              <span className="text-xs font-bold text-gray-900">142 km</span>
            </div>
            <div className="flex justify-between gap-8">
              <span className="text-xs text-gray-500">Visits</span>
              <span className="text-xs font-bold text-gray-900">28</span>
            </div>
            <div className="flex justify-between gap-8">
              <span className="text-xs text-gray-500">Efficiency</span>
              <span className="text-xs font-bold text-green-600">94%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// In Person Action View (Connected to Live Map Engine)
const InPersonActionView = () => {
  return (
    <div className="h-full w-full relative">
      <MapEngine />
    </div>
  );
};

// Field Data Management View (Management section - Data Collection board)
const DataCollectionView = () => {
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showMobileUI, setShowMobileUI] = useState(false);

  if (showHeatmap) {
    return (
      <div className="h-full flex flex-col p-6">
        <div className="mb-4">
          <button
            onClick={() => setShowHeatmap(false)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-bold"
          >
            <ArrowRight className="rotate-180" size={20} />
            Back to Field Management
          </button>
        </div>
        <div className="flex-1">
          <CollectorHeatmap />
        </div>
      </div>
    );
  }

  if (showMobileUI) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 bg-white border-b">
          <button
            onClick={() => setShowMobileUI(false)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-bold"
          >
            <ArrowRight className="rotate-180" size={20} />
            Back to Field Management
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-slate-100 to-slate-200">
          {/* Mobile Simulator */}
          <div className="w-[360px] h-[720px] flex-shrink-0 bg-gray-900 rounded-[2rem] p-3 shadow-2xl relative">
            <div className="bg-gray-100 w-full h-full rounded-[1.5rem] overflow-hidden flex flex-col relative">
              <div className="flex-1 relative overflow-y-auto bg-gray-50">
                <div className="p-4 pt-8 h-full relative">
                  <h2 className="text-xl font-bold text-gray-800 mb-1">Hello, Majid</h2>
                  <p className="text-xs text-gray-500 mb-6">Field Collector Dashboard</p>
                  
                  <div className="grid grid-cols-2 gap-3 mb-20">
                    <button className="bg-blue-600 text-white p-4 rounded-2xl shadow-lg h-28 flex flex-col items-center justify-center">
                      <Navigation size={28} />
                      <span className="font-bold mt-2">Routes</span>
                    </button>
                    <button className="bg-white text-gray-700 p-4 rounded-2xl shadow-sm border h-28 flex flex-col items-center justify-center">
                      <ClipboardList size={28} />
                      <span className="font-bold text-sm mt-2">Evaluation</span>
                    </button>
                    <button className="bg-white text-gray-700 p-4 rounded-2xl shadow-sm border h-28 flex flex-col items-center justify-center">
                      <Package size={28} />
                      <span className="font-bold text-sm mt-2">Delivery</span>
                    </button>
                    <button className="bg-white text-gray-700 p-4 rounded-2xl shadow-sm border h-28 flex flex-col items-center justify-center">
                      <UserCheck size={28} />
                      <span className="font-bold text-sm mt-2">Follow-up</span>
                    </button>
                    <button className="col-span-2 bg-white text-gray-700 p-4 rounded-2xl shadow-sm border h-20 flex flex-col items-center justify-center">
                      <AlertTriangle size={28} className="mb-2 text-red-500" />
                      <span className="font-bold text-sm">Urgent</span>
                    </button>
                  </div>
                  
                  <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
                    <div className="flex gap-3">
                      <button className="w-12 h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600">
                        <MessageSquare size={20} />
                      </button>
                      <button className="w-12 h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-600">
                        <Bell size={20} />
                      </button>
                    </div>
                    <button className="w-16 h-16 bg-white rounded-full shadow-xl border-4 border-gray-100 flex items-center justify-center text-blue-600 transform -translate-y-4">
                      <MapPin size={28} fill="currentColor" />
                    </button>
                    <button className="flex items-center gap-2 p-1 pr-3 rounded-full bg-gray-100">
                      <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-white font-bold text-xs">MJ</div>
                      <div className="flex flex-col items-start leading-none">
                        <span className="text-[10px] font-bold text-gray-700">Balance</span>
                        <span className="text-[10px] text-green-600 font-bold">$3.2k</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6">
      {/* Field Data Management Panel */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col overflow-hidden">
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Field Data Management</h3>
            <p className="text-sm text-gray-500">Monitor field collectors and dispatch new routes</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowMobileUI(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-pink-600 text-white rounded-lg font-bold hover:from-orange-700 hover:to-pink-700 shadow-lg transition-all"
            >
              <Smartphone size={18} />
              View Mobile App
            </button>
            <button
              onClick={() => setShowHeatmap(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 shadow-lg transition-all"
            >
              <MapIcon size={18} />
              View Live Heatmap
            </button>
          </div>
        </div>
        
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Active Collectors */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                <Users size={18} className="mr-2" /> Active Collectors
              </h4>
              <div className="space-y-3">
                <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">MJ</div>
                    <div>
                      <div className="font-bold text-sm">Majid Survey</div>
                      <div className="text-xs text-gray-500">Route: York Mills</div>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-bold">Active</span>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">KD</div>
                    <div>
                      <div className="font-bold text-sm">Ken Delivery</div>
                      <div className="text-xs text-gray-500">Task: Sign Install</div>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-bold">En Route</span>
                </div>
              </div>
            </div>

            {/* Dispatch New Task */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                <SettingsIcon size={18} className="mr-2" /> Dispatch New Task
              </h4>
              <div className="space-y-3">
                <select className="w-full p-2.5 border rounded-lg bg-white text-sm">
                  <option>Route Survey</option>
                  <option>Specific Verification</option>
                  <option>Material Delivery</option>
                </select>
                <input className="w-full p-2.5 border rounded-lg bg-white text-sm" placeholder="Details / Address" />
                <div className="grid grid-cols-2 gap-2">
                  <select className="w-full p-2.5 border rounded-lg bg-white text-sm">
                    <option>Normal Priority</option>
                    <option>High Priority</option>
                    <option>Urgent</option>
                  </select>
                  <select className="w-full p-2.5 border rounded-lg bg-white text-sm">
                    <option>Auto Assign</option>
                    <option>Majid</option>
                    <option>Ken</option>
                  </select>
                </div>
                <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 shadow-sm">
                  Dispatch Task
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="md:col-span-2 lg:col-span-1 bg-gray-50 p-4 rounded-xl border border-gray-200">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                <Activity size={18} className="mr-2" /> Recent Field Activity
              </h4>
              <div className="space-y-2">
                {INITIAL_TASKS.slice(0, 3).map(task => (
                  <div key={task.id} className="bg-white p-3 rounded-lg border border-gray-200">
                    <div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${task.priority === 'High' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                        {task.type}
                      </span>
                      <div className="font-bold text-sm mt-1">{task.title}</div>
                      <div className="text-xs text-gray-500">Assignee: {task.assignee}</div>
                    </div>
                    <span className="text-xs font-bold text-blue-600 mt-1 block">{task.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MANAGEMENT VIEW ====================

const ManagementView = ({ activeSubBoard, setActiveSubBoard, onOpenCRM }) => {
  const [activeTab, setActiveTab] = useState('AI_Control');

  if (!activeSubBoard) {
    return (
      <div className="p-8 h-full overflow-y-auto bg-gray-50">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Management Console</h2>
        <p className="text-gray-500 mb-8">Centralized control for data, logistics, and enterprise health.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button onClick={() => setActiveSubBoard('Lead Generation Settings')} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all group text-left">
            <div className="p-4 bg-blue-50 rounded-xl w-fit mb-4 group-hover:bg-blue-100">
              <Settings size={32} className="text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Lead Generation Settings</h3>
            <p className="text-sm text-gray-500 mt-2">Configure data collection routes, API integrations, and lead sources.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Evaluation & Sorting')} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-orange-300 transition-all group text-left">
            <div className="p-4 bg-orange-50 rounded-xl w-fit mb-4 group-hover:bg-orange-100">
              <ClipboardList size={32} className="text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Evaluation & Sorting</h3>
            <p className="text-sm text-gray-500 mt-2">QA Center for 'Backed' data. Re-inject valid leads.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Parking')} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-orange-300 transition-all group text-left">
            <div className="p-4 bg-orange-50 rounded-xl w-fit mb-4 group-hover:bg-orange-100">
              <Archive size={32} className="text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Parking</h3>
            <p className="text-sm text-gray-500 mt-2">Holding area for future leads. Schedule wake-up dates.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Enterprise')} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-green-300 transition-all group text-left">
            <div className="p-4 bg-green-50 rounded-xl w-fit mb-4 group-hover:bg-green-100">
              <Building size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Enterprise Control</h3>
            <p className="text-sm text-gray-500 mt-2">AI-driven monitoring of staff performance and system health.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Communication')} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-indigo-300 transition-all group text-left">
            <div className="p-4 bg-indigo-50 rounded-xl w-fit mb-4 group-hover:bg-indigo-100">
              <MessageCircle size={32} className="text-indigo-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Communication Hub</h3>
            <p className="text-sm text-gray-500 mt-2">Team messaging, channels, and internal communication center.</p>
          </button>
        </div>
      </div>
    );
  }

// Evaluation & Sorting View
  if (activeSubBoard === 'Evaluation & Sorting') {
    return (
      <EvaluationSortingBoard 
        onClose={() => setActiveSubBoard(null)}
        onOpenCRM={onOpenCRM}
      />
    );
  }

  // Parking View
  if (activeSubBoard === 'Parking') {
    const PHASE_COL_COLORS = [
      ['bg-blue-100 text-blue-800', 'border-blue-300'],
      ['bg-indigo-100 text-indigo-800', 'border-indigo-300'],
      ['bg-orange-100 text-orange-800', 'border-orange-300'],
      ['bg-pink-100 text-pink-800', 'border-pink-300'],
      ['bg-rose-100 text-rose-800', 'border-rose-300'],
      ['bg-orange-100 text-orange-800', 'border-orange-300'],
      ['bg-emerald-100 text-emerald-800', 'border-emerald-300'],
      ['bg-slate-100 text-slate-800', 'border-slate-300'],
    ];
    // Columns derive from the canonical phase list; legacy `phaseN` keys are normalized below.
    const PHASES = [
      ...CANON_PHASES.map(([id, label], i) => ({ id, label: `${id.toUpperCase()} · ${label}`, color: (PHASE_COL_COLORS[i] || PHASE_COL_COLORS[7])[0], borderColor: (PHASE_COL_COLORS[i] || PHASE_COL_COLORS[7])[1] })),
      { id: 'mls', label: 'MLS: Renovation', color: 'bg-cyan-100 text-cyan-800', borderColor: 'border-cyan-300' },
    ];
    // Legacy mock keys (phase0..phase5) map onto canonical p0..p5.
    const normPhaseKey = (k) => (typeof k === 'string' && k.startsWith('phase')) ? ('p' + k.slice(5)) : k;

    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-orange-50">
        <div className="bg-white border-b shadow-sm px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => setActiveSubBoard(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <ArrowRight className="rotate-180" size={20} />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Archive size={28} className="text-orange-600" />
                Parking
              </h2>
              <p className="text-sm text-gray-500">Scheduled leads organized by target phase - ready for injection after previous approach</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-8 gap-3">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <Archive size={16} className="text-orange-600" />
                <div className="text-xs font-bold text-orange-600 uppercase">Total Parked</div>
              </div>
              <div className="text-3xl font-bold text-orange-900">{TERMINAL_DATA.length}</div>
              <div className="text-[10px] text-orange-600 mt-1">All Phases</div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-blue-600" />
                <div className="text-xs font-bold text-blue-600 uppercase">Phase 0</div>
              </div>
              <div className="text-3xl font-bold text-blue-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'phase0').length}
              </div>
              <div className="text-[10px] text-blue-600 mt-1">Survey</div>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl border border-indigo-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-indigo-600" />
                <div className="text-xs font-bold text-indigo-600 uppercase">Phase 1</div>
              </div>
              <div className="text-3xl font-bold text-indigo-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'phase1').length}
              </div>
              <div className="text-[10px] text-indigo-600 mt-1">Foundation</div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-orange-600" />
                <div className="text-xs font-bold text-orange-600 uppercase">Phase 2</div>
              </div>
              <div className="text-3xl font-bold text-orange-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'phase2').length}
              </div>
              <div className="text-[10px] text-orange-600 mt-1">Framing</div>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-xl border border-pink-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-pink-600" />
                <div className="text-xs font-bold text-pink-600 uppercase">Phase 3</div>
              </div>
              <div className="text-3xl font-bold text-pink-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'phase3').length}
              </div>
              <div className="text-[10px] text-pink-600 mt-1">MEC/Drywall</div>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-4 rounded-xl border border-rose-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-rose-600" />
                <div className="text-xs font-bold text-rose-600 uppercase">Phase 4</div>
              </div>
              <div className="text-3xl font-bold text-rose-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'phase4').length}
              </div>
              <div className="text-[10px] text-rose-600 mt-1">Finishing</div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-amber-600" />
                <div className="text-xs font-bold text-amber-600 uppercase">Phase 5</div>
              </div>
              <div className="text-3xl font-bold text-amber-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'phase5').length}
              </div>
              <div className="text-[10px] text-amber-600 mt-1">Landscaping</div>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-xl border border-cyan-200">
              <div className="flex items-center gap-2 mb-2">
                <Layers size={16} className="text-cyan-600" />
                <div className="text-xs font-bold text-cyan-600 uppercase">MLS</div>
              </div>
              <div className="text-3xl font-bold text-cyan-900">
                {TERMINAL_DATA.filter(t => t.targetPhase === 'mls').length}
              </div>
              <div className="text-[10px] text-cyan-600 mt-1">Renovation</div>
            </div>
          </div>
        </div>
        
        <div className="flex-1 p-6 overflow-x-auto">
          <div className="flex gap-4 h-full min-w-max">
            {PHASES.map(phase => {
              const phaseLeads = TERMINAL_DATA.filter(t => normPhaseKey(t.targetPhase) === phase.id);
              
              return (
                <div key={phase.id} className={`w-80 flex flex-col bg-white rounded-xl border-2 ${phase.borderColor} shadow-lg`}>
                  <div className={`p-3 ${phase.color} rounded-t-xl flex items-center justify-between`}>
                    <span className="font-bold text-sm">{phase.label}</span>
                    <span className="bg-white/70 px-2 py-0.5 rounded text-xs font-bold">{phaseLeads.length}</span>
                  </div>
                  <div className="p-2 flex-1 overflow-y-auto space-y-2">
                    {phaseLeads.length === 0 ? (
                      <div className="text-center py-8 text-xs text-gray-400">
                        No scheduled leads for this phase
                      </div>
                    ) : (
                      phaseLeads.map(lead => (
                        <div 
                          key={lead.id} 
                          className={`p-3 rounded-lg border-2 ${
                            lead.priority === 'urgent' 
                              ? 'bg-red-50 border-red-300 border-l-4' 
                              : lead.priority === 'high'
                              ? 'bg-yellow-50 border-yellow-300 border-l-4'
                              : 'bg-gray-50 border-gray-200'
                          } hover:shadow-md transition-shadow cursor-pointer`}
                        >
                          {/* Priority Badge */}
                          {lead.priority === 'urgent' && (
                            <div className="flex items-center gap-1 mb-2">
                              <span className="text-[8px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                <AlertTriangle size={8} />
                                URGENT
                              </span>
                            </div>
                          )}
                          
                          {/* Address & Business */}
                          <div className="font-bold text-sm text-gray-900">{lead.address}</div>
                          <div className="text-xs text-gray-600 mb-2">{lead.business}</div>
                          
                          {/* Contact */}
                          <div className="text-[10px] text-gray-700 mb-2">
                            <div className="font-bold">{lead.contact}</div>
                            <div>{lead.phone}</div>
                          </div>
                          
                          {/* Next Action */}
                          <div className="bg-blue-100 text-blue-800 text-[9px] font-bold px-2 py-1 rounded mb-2">
                            {lead.nextAction}
                          </div>
                          
                          {/* Wake Date */}
                          <div className="text-xs text-gray-500 flex items-center">
                            <CalendarDays size={12} className="mr-1" /> 
                            <span className="font-bold">Inject on:</span> {lead.wakeDate}
                          </div>
                          
                          {/* Action Button */}
                          <button className="w-full mt-2 py-1.5 bg-orange-600 text-white rounded text-xs font-bold hover:bg-orange-700 transition-colors flex items-center justify-center gap-1">
                            <Forward size={12} />
                            Push to {phase.label.split(':')[0]}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Enterprise Control View
  if (activeSubBoard === 'Enterprise') {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="border-b px-6 py-4 flex gap-4 items-center">
          <button onClick={() => setActiveSubBoard(null)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowRight className="rotate-180" size={20} />
          </button>
          <div className="h-8 w-[1px] bg-gray-200"></div>
          <button onClick={() => setActiveTab('AI_Control')} className={`pb-2 text-sm font-bold ${activeTab === 'AI_Control' ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-500'}`}>
            <BrainCircuit size={16} className="inline mr-1" /> AI Control Tower
          </button>
          <button onClick={() => setActiveTab('HR')} className={`pb-2 text-sm font-bold ${activeTab === 'HR' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}>
            HR Management
          </button>
        </div>
        
        <div className="flex-1 p-6 bg-gray-50 overflow-y-auto">
          {activeTab === 'AI_Control' && (
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="bg-indigo-900 text-white p-6 rounded-2xl shadow-lg flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold mb-1">System Health: 94%</h2>
                  <p className="text-indigo-200 text-sm">AI is monitoring 12 pipelines and 8 staff members.</p>
                </div>
                <div className="h-16 w-16 bg-white/10 rounded-full flex items-center justify-center animate-pulse">
                  <Activity size={32} />
                </div>
              </div>
              
              <h3 className="font-bold text-gray-800 text-lg">Active Alerts</h3>
              <div className="space-y-3">
                <div className="bg-white p-4 rounded-xl border-l-4 border-yellow-500 shadow-sm flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-900 flex items-center gap-2">
                      <AlertOctagon size={16} className="text-yellow-500" /> Performance Warning
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Telemarketer <span className="font-bold">Sarah Sales</span> has low conversion (3%) this week.
                    </p>
                  </div>
                  <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700">
                    <MessageSquare size={12} className="inline mr-1" /> Message Sarah
                  </button>
                </div>
                
                <div className="bg-white p-4 rounded-xl border-l-4 border-red-500 shadow-sm flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-gray-900 flex items-center gap-2">
                      <Ban size={16} className="text-red-500" /> Data Quality Alert
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      High "Backed" rate (15%) detected on <span className="font-bold">Phase 2: Framing</span> board.
                    </p>
                  </div>
                  <button className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700">
                    Audit Phase 2
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'HR' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                  <Users className="mr-2" size={18} /> Active Staff
                </h3>
                <div className="space-y-3">
                  {MOCK_USER_DATABASE.filter(u => u.type === 'Staff').slice(0, 3).map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                          {user.avatar}
                        </div>
                        <div>
                          <div className="text-sm font-bold">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.role}</div>
                        </div>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{user.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Lead Generation Settings sub-view
  if (activeSubBoard === 'Lead Generation Settings') {
    return <LeadGenerationSettingsView onClose={() => setActiveSubBoard(null)} />;
  }

  // Communication sub-view
  if (activeSubBoard === 'Communication') {
    return <CommunicationView onClose={() => setActiveSubBoard(null)} />;
  }

  return null;
};

// ==================== SALES & PR VIEW ====================

const SalesPRView = ({ activeSubBoard, setActiveSubBoard, onCardClick, onOpenCRM, onOpenTakeoff, activeNationalityFilter, setActiveNationalityFilter, activeTradeFilter, setActiveTradeFilter }) => {
  const [configuringClient, setConfiguringClient] = useState(null);
  const [showNetworkVisualizer, setShowNetworkVisualizer] = useState(false);
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [activePackageFilter, setActivePackageFilter] = useState('all');
  
  // Custom Filters State
  const [customNationalities, setCustomNationalities] = useState(['Persian', 'Chinese', 'Italian', 'Korean', 'Spanish', 'Canadian']);
  const [customTrades, setCustomTrades] = useState(['Masonry', 'Drywall', 'Framing', 'Electrical', 'Plumbing', 'Painting', 'Window Installation', 'Equipment Rental', 'Real Estate', 'General Contractor', 'Owner', 'Developer']);
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [showTradeDropdown, setShowTradeDropdown] = useState(false);
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [tradeSearch, setTradeSearch] = useState('');

  const handleConfigComplete = () => {
    setConfiguringClient(null);
    alert('Configuration Complete!');
  };

  if (!activeSubBoard) {
    return (
      <div className="p-8 h-full overflow-y-auto bg-slate-50">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Sales & P.R.</h2>
        <p className="text-slate-500 mb-8">Network Monetization & B2B Client Acquisition.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button onClick={() => setActiveSubBoard('Networking')} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-blue-300 transition-all group text-left">
            <div className="p-4 bg-blue-50 rounded-xl w-fit mb-4">
              <Network size={32} className="text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Networking</h3>
            <p className="text-sm text-slate-500 mt-2">Manage construction network relationships between demand and supply.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Supply Sales (B2B)')} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-orange-300 transition-all group text-left">
            <div className="p-4 bg-orange-50 rounded-xl w-fit mb-4">
              <HeartHandshake size={32} className="text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Supply Sales (B2B)</h3>
            <p className="text-sm text-slate-500 mt-2">B2B pipeline from prospecting to live service.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Close to Deal')} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-green-300 transition-all group text-left">
            <div className="p-4 bg-green-50 rounded-xl w-fit mb-4">
              <FileSignature size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Close to Deal</h3>
            <p className="text-sm text-slate-500 mt-2">Final stages: agreements, configuration, launch.</p>
          </button>
          
          <button onClick={() => setActiveSubBoard('Leads in Sales')} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-orange-300 transition-all group text-left">
            <div className="p-4 bg-orange-50 rounded-xl w-fit mb-4">
              <List size={32} className="text-orange-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">Leads in Sales</h3>
            <p className="text-sm text-slate-500 mt-2">Table view of all active sales leads.</p>
          </button>
        </div>
      </div>
    );
  }

  // Networking View
  if (activeSubBoard === 'Networking') {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setActiveSubBoard(null)} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowRight className="rotate-180" size={20} />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Networking Database</h2>
              <p className="text-sm text-gray-500">Manage all business relationships</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowNetworkVisualizer(true)}
            className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all font-bold"
          >
            <Network size={20} />
            <span>3D Network Visualizer</span>
            <span className="text-xs bg-white/20 px-2 py-1 rounded">NEW</span>
          </button>
        </div>
        
        {showNetworkVisualizer && (
          <ConstructionNetworkVisualizer onClose={() => setShowNetworkVisualizer(false)} />
        )}
        
        {/* Compact Filters Section - Trello Style */}
        <div className="bg-white border-b px-6 py-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-500 uppercase">Filters:</span>
            
            {/* Nationality Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNationalityDropdown(!showNationalityDropdown);
                  setShowTradeDropdown(false);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  activeNationalityFilter !== 'all'
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Tag size={14} />
                <span>{activeNationalityFilter === 'all' ? 'Nationality' : activeNationalityFilter}</span>
                <ChevronDown size={14} />
              </button>
              
              {showNationalityDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowNationalityDropdown(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-200 z-20">
                    <div className="p-3 border-b border-slate-200">
                      <div className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-2">
                        <Search size={14} className="text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search or create..."
                          value={nationalitySearch}
                          onChange={(e) => setNationalitySearch(e.target.value)}
                          className="flex-1 bg-transparent text-xs outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto p-2">
                      {/* All option */}
                      <button
                        onClick={() => {
                          setActiveNationalityFilter('all');
                          setShowNationalityDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded text-xs font-bold transition-all ${
                          activeNationalityFilter === 'all'
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>All Nationalities</span>
                          <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[10px]">
                            {[...NETWORKING_DATA.demand, ...NETWORKING_DATA.supply, ...NETWORKING_DATA.real_estate].length}
                          </span>
                        </div>
                      </button>
                      
                      {/* Existing nationalities */}
                      {customNationalities
                        .filter(n => n.toLowerCase().includes(nationalitySearch.toLowerCase()))
                        .map(nationality => {
                          const count = [...NETWORKING_DATA.demand, ...NETWORKING_DATA.supply, ...NETWORKING_DATA.real_estate]
                            .filter(item => item.nationality === nationality).length;
                          return (
                            <button
                              key={nationality}
                              onClick={() => {
                                setActiveNationalityFilter(nationality);
                                setShowNationalityDropdown(false);
                              }}
                              className={`w-full text-left px-3 py-2 rounded text-xs font-bold transition-all flex items-center justify-between ${
                                activeNationalityFilter === nationality
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <span>{nationality}</span>
                              <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[10px]">{count}</span>
                            </button>
                          );
                        })}
                      
                      {/* Create new nationality */}
                      {nationalitySearch && !customNationalities.some(n => n.toLowerCase() === nationalitySearch.toLowerCase()) && (
                        <button
                          onClick={() => {
                            setCustomNationalities([...customNationalities, nationalitySearch]);
                            setNationalitySearch('');
                          }}
                          className="w-full text-left px-3 py-2 rounded text-xs font-bold text-green-600 hover:bg-green-50 transition-all flex items-center gap-2 border-t border-slate-200 mt-2 pt-3"
                        >
                          <Plus size={14} />
                          <span>Create "{nationalitySearch}"</span>
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Trade Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTradeDropdown(!showTradeDropdown);
                  setShowNationalityDropdown(false);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                  activeTradeFilter !== 'all'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Briefcase size={14} />
                <span>{activeTradeFilter === 'all' ? 'Trade' : activeTradeFilter}</span>
                <ChevronDown size={14} />
              </button>
              
              {showTradeDropdown && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowTradeDropdown(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-xl border border-slate-200 z-20">
                    <div className="p-3 border-b border-slate-200">
                      <div className="flex items-center gap-2 bg-slate-50 rounded-md px-3 py-2">
                        <Search size={14} className="text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search or create..."
                          value={tradeSearch}
                          onChange={(e) => setTradeSearch(e.target.value)}
                          className="flex-1 bg-transparent text-xs outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    
                    <div className="max-h-64 overflow-y-auto p-2">
                      {/* All option */}
                      <button
                        onClick={() => {
                          setActiveTradeFilter('all');
                          setShowTradeDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded text-xs font-bold transition-all ${
                          activeTradeFilter === 'all'
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>All Trades</span>
                          <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[10px]">
                            {[...NETWORKING_DATA.demand, ...NETWORKING_DATA.supply, ...NETWORKING_DATA.real_estate].length}
                          </span>
                        </div>
                      </button>
                      
                      {/* Existing trades */}
                      {customTrades
                        .filter(t => t.toLowerCase().includes(tradeSearch.toLowerCase()))
                        .map(trade => {
                          const count = [...NETWORKING_DATA.demand, ...NETWORKING_DATA.supply, ...NETWORKING_DATA.real_estate]
                            .filter(item => item.trade === trade).length;
                          return (
                            <button
                              key={trade}
                              onClick={() => {
                                setActiveTradeFilter(trade);
                                setShowTradeDropdown(false);
                              }}
                              className={`w-full text-left px-3 py-2 rounded text-xs font-bold transition-all flex items-center justify-between ${
                                activeTradeFilter === trade
                                  ? 'bg-orange-50 text-orange-700'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <span>{trade}</span>
                              <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[10px]">{count}</span>
                            </button>
                          );
                        })}
                      
                      {/* Create new trade */}
                      {tradeSearch && !customTrades.some(t => t.toLowerCase() === tradeSearch.toLowerCase()) && (
                        <button
                          onClick={() => {
                            setCustomTrades([...customTrades, tradeSearch]);
                            setTradeSearch('');
                          }}
                          className="w-full text-left px-3 py-2 rounded text-xs font-bold text-green-600 hover:bg-green-50 transition-all flex items-center gap-2 border-t border-slate-200 mt-2 pt-3"
                        >
                          <Plus size={14} />
                          <span>Create "{tradeSearch}"</span>
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Active Filters Display */}
            {(activeNationalityFilter !== 'all' || activeTradeFilter !== 'all') && (
              <>
                <div className="h-4 w-px bg-slate-300" />
                <div className="flex items-center gap-2">
                  {activeNationalityFilter !== 'all' && (
                    <span className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">
                      {activeNationalityFilter}
                      <X 
                        size={12} 
                        className="cursor-pointer hover:text-blue-900" 
                        onClick={() => setActiveNationalityFilter('all')}
                      />
                    </span>
                  )}
                  {activeTradeFilter !== 'all' && (
                    <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-bold">
                      {activeTradeFilter}
                      <X 
                        size={12} 
                        className="cursor-pointer hover:text-orange-900" 
                        onClick={() => setActiveTradeFilter('all')}
                      />
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setActiveNationalityFilter('all');
                      setActiveTradeFilter('all');
                    }}
                    className="text-xs text-slate-500 hover:text-slate-700 underline"
                  >
                    Clear all
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Demand Side */}
            <div className="space-y-4">
              <h3 className="font-bold text-slate-500 uppercase text-xs tracking-wider">Demand Side (Owners)</h3>
              {NETWORKING_DATA.demand
                .filter(item => 
                  (activeNationalityFilter === 'all' || item.nationality === activeNationalityFilter) &&
                  (activeTradeFilter === 'all' || item.trade === activeTradeFilter)
                )
                .map(item => (
                <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{item.type}</span>
                    <div className="flex gap-1">
                      <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded font-bold">{item.nationality}</span>
                      <span className="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded font-bold">{item.trade}</span>
                    </div>
                  </div>
                  <h4 className="font-bold text-slate-800 mt-2">{item.name}</h4>
                  <p className="text-xs text-slate-500 mt-1 flex items-center">
                    <Briefcase size={12} className="mr-1" /> Active Projects: {item.activeProjects}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Supply Side */}
            <div className="space-y-4">
              <h3 className="font-bold text-slate-500 uppercase text-xs tracking-wider">Supply Side (Vendors)</h3>
              {NETWORKING_DATA.supply
                .filter(item => 
                  (activeNationalityFilter === 'all' || item.nationality === activeNationalityFilter) &&
                  (activeTradeFilter === 'all' || item.trade === activeTradeFilter)
                )
                .map(item => (
                <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 border-l-4 border-l-orange-400 hover:shadow-md transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold bg-orange-50 text-orange-600 px-2 py-0.5 rounded">{item.type}</span>
                    <div className="flex gap-1">
                      <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded font-bold">{item.nationality}</span>
                      <span className="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded font-bold">{item.trade}</span>
                    </div>
                  </div>
                  <h4 className="font-bold text-slate-800 mt-2">{item.name}</h4>
                  <p className="text-xs text-slate-500 mt-1">Status: <span className="font-bold">{item.status}</span></p>
                </div>
              ))}
            </div>
            
            {/* Real Estate */}
            <div className="space-y-4">
              <h3 className="font-bold text-slate-500 uppercase text-xs tracking-wider">Real Estate / MLS</h3>
              {NETWORKING_DATA.real_estate
                .filter(item => 
                  (activeNationalityFilter === 'all' || item.nationality === activeNationalityFilter) &&
                  (activeTradeFilter === 'all' || item.trade === activeTradeFilter)
                )
                .map(item => (
                <div key={item.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-all cursor-pointer">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded font-bold">{item.nationality}</span>
                    <span className="text-[10px] bg-green-100 text-green-600 px-2 py-0.5 rounded font-bold">{item.trade}</span>
                  </div>
                  <h4 className="font-bold text-slate-800">{item.name}</h4>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-slate-500">{item.type}</span>
                    <span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">{item.leads} Leads</span>
                  </div>
                </div>
              ))}
            </div>
            
          </div>
        </div>
      </div>
    );
  }

  // B2B Pipeline View
  if (activeSubBoard === 'Supply Sales (B2B)') {
    // Filter leads based on package selection
    const filteredB2BLeads = activePackageFilter === 'all' 
      ? B2B_LEADS 
      : B2B_LEADS.filter(lead => lead.packages && lead.packages.includes(activePackageFilter));

    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setActiveSubBoard(null)} className="p-2 hover:bg-gray-100 rounded-full">
                <ArrowRight className="rotate-180" size={20} />
              </button>
              <div>
                <h2 className="text-xl font-bold text-gray-800">B2B Sales Pipeline</h2>
                <p className="text-sm text-gray-500">Track prospects from initial contact to launch</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddBusinessModal(true)}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-orange-700 transition-all shadow-md flex items-center gap-2"
            >
              <Plus size={18} />
              Add New Business
            </button>
          </div>
          
          {/* Package Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-600">Filter by Package:</span>
            <button
              onClick={() => setActivePackageFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePackageFilter === 'all'
                  ? 'bg-slate-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All ({B2B_LEADS.length})
            </button>
            <button
              onClick={() => setActivePackageFilter('telemarketing')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePackageFilter === 'telemarketing'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              Telemarketing ({B2B_LEADS.filter(l => l.packages?.includes('telemarketing')).length})
            </button>
            <button
              onClick={() => setActivePackageFilter('digital_marketing')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePackageFilter === 'digital_marketing'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Digital Marketing ({B2B_LEADS.filter(l => l.packages?.includes('digital_marketing')).length})
            </button>
            <button
              onClick={() => setActivePackageFilter('in_person_marketing')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePackageFilter === 'in_person_marketing'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              In Person Marketing ({B2B_LEADS.filter(l => l.packages?.includes('in_person_marketing')).length})
            </button>
            <button
              onClick={() => setActivePackageFilter('delivery')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activePackageFilter === 'delivery'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              Delivery ({B2B_LEADS.filter(l => l.packages?.includes('delivery')).length})
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-x-auto p-6">
          <div className="flex gap-4 h-full min-w-max">
            {B2B_PIPELINE_COLUMNS.map(col => {
              const colLeads = filteredB2BLeads.filter(l => l.status === col.id);
              return (
                <div key={col.id} className="w-80 flex flex-col h-full rounded-xl bg-slate-200/50 border border-slate-200">
                  <div className="p-3 rounded-t-xl border-b border-slate-200 bg-white flex justify-between items-center">
                    <span className="font-bold text-sm text-slate-800">{col.label}</span>
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-bold text-slate-600">{colLeads.length}</span>
                  </div>
                  <div className="p-2 flex-1 overflow-y-auto space-y-2">
                    {colLeads.map(lead => (
                      <B2BCard
                        key={lead.id}
                        lead={lead}
                        onClick={() => onCardClick(lead)}
                        onConfigure={setConfiguringClient}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {configuringClient && (
          <ClientConfigurationModal
            client={configuringClient}
            onClose={() => setConfiguringClient(null)}
            onComplete={handleConfigComplete}
          />
        )}

        <AddNewBusinessModal
          isOpen={showAddBusinessModal}
          onClose={() => setShowAddBusinessModal(false)}
        />
      </div>
    );
  }

  // Close to Deal View
  if (activeSubBoard === 'Close to Deal') {
    return (
      <div className="h-full flex flex-col bg-gray-50 p-6 overflow-y-auto">
        <div className="mb-6">
          <button onClick={() => setActiveSubBoard(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
            <ArrowRight className="rotate-180" size={20} />
            <span className="font-bold">Back to Sales & P.R.</span>
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Close to Deal</h2>
          <p className="text-gray-500">Final stages before launching service</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h3 className="font-bold text-slate-800 mb-4">Agreement Phase</h3>
            <div className="space-y-3">
              {B2B_LEADS.filter(l => ['negotiation', 'agreement'].includes(l.status)).map(lead => (
                <div key={lead.id} className="p-3 border rounded-lg hover:shadow-md transition-all cursor-pointer" onClick={() => onCardClick(lead)}>
                  <h4 className="font-bold text-sm">{lead.name}</h4>
                  <p className="text-xs text-gray-500 mt-1">{lead.value}</p>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h3 className="font-bold text-slate-800 mb-4">Configuration</h3>
            <div className="space-y-3">
              {B2B_LEADS.filter(l => l.status === 'config_pending').map(lead => (
                <div key={lead.id} className="p-3 border rounded-lg border-l-4 border-l-orange-500 hover:shadow-md transition-all cursor-pointer">
                  <h4 className="font-bold text-sm">{lead.name}</h4>
                  <button
                    onClick={() => setConfiguringClient(lead)}
                    className="w-full mt-2 py-1.5 bg-orange-600 text-white text-xs font-bold rounded hover:bg-orange-700"
                  >
                    <SettingsIcon size={12} className="inline mr-1" /> Configure
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h3 className="font-bold text-slate-800 mb-4">Ready for Launch</h3>
            <div className="space-y-3">
              {B2B_LEADS.filter(l => l.status === 'live').map(lead => (
                <div key={lead.id} className="p-3 border rounded-lg bg-green-50 border-green-200 hover:shadow-md transition-all cursor-pointer" onClick={() => onCardClick(lead)}>
                  <h4 className="font-bold text-sm text-green-900">{lead.name}</h4>
                  <p className="text-xs text-green-700 mt-1">Service Live</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {configuringClient && (
          <ClientConfigurationModal
            client={configuringClient}
            onClose={() => setConfiguringClient(null)}
            onComplete={handleConfigComplete}
          />
        )}
      </div>
    );
  }

  // Leads in Sales Table View
  if (activeSubBoard === 'Leads in Sales') {
    return (
      <div className="h-full flex flex-col bg-gray-50 p-6 overflow-y-auto">
        <div className="mb-6">
          <button onClick={() => setActiveSubBoard(null)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
            <ArrowRight className="rotate-180" size={20} />
            <span className="font-bold">Back to Sales & P.R.</span>
          </button>
          <h2 className="text-2xl font-bold text-gray-800">Leads in Sales</h2>
          <p className="text-gray-500">All active B2B opportunities</p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase border-b">
              <tr>
                <th className="p-4">Client Name</th>
                <th className="p-4">Service Type</th>
                <th className="p-4">Est. Value</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {B2B_LEADS.map(lead => (
                <tr key={lead.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onCardClick(lead)}>
                  <td className="p-4 font-bold">{lead.name}</td>
                  <td className="p-4">{lead.value}</td>
                  <td className="p-4">Monthly Recurring</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      lead.status === 'live' ? 'bg-green-100 text-green-700' :
                      lead.status === 'config_pending' ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {lead.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button className="text-blue-600 font-bold text-sm hover:underline">View Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
};

// ==================== SERVICES VIEW ====================

const ServicesView = ({ onCardClick, onOpenCRM, onOpenTakeoff }) => {
  const [activeBoard, setActiveBoard] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState('all');

  if (activeBoard === 'sales_estimates') {
    // Define lead sources from marketing boards
    const leadSources = [
      { id: 'all', label: 'All Leads', color: 'bg-slate-500', count: SALES_CARDS.length },
      { id: 'phase_0', label: 'Phase 0', color: 'bg-blue-500', count: SALES_CARDS.filter(c => c.source?.includes('Phase 0')).length },
      { id: 'phase_1', label: 'Phase 1', color: 'bg-indigo-500', count: SALES_CARDS.filter(c => c.source?.includes('Phase 1')).length },
      { id: 'phase_2', label: 'Phase 2', color: 'bg-orange-500', count: SALES_CARDS.filter(c => c.source?.includes('Phase 2')).length },
      { id: 'phase_3', label: 'Phase 3', color: 'bg-pink-500', count: SALES_CARDS.filter(c => c.source?.includes('Phase 3')).length },
      { id: 'phase_4', label: 'Phase 4', color: 'bg-rose-500', count: SALES_CARDS.filter(c => c.source?.includes('Phase 4')).length },
      { id: 'phase_5', label: 'Phase 5', color: 'bg-orange-500', count: SALES_CARDS.filter(c => c.source?.includes('Phase 5')).length },
      { id: 'seasonal', label: 'Seasonal', color: 'bg-green-500', count: SALES_CARDS.filter(c => c.source?.includes('Seasonal')).length },
      { id: 'mls', label: 'MLS', color: 'bg-cyan-500', count: SALES_CARDS.filter(c => c.source?.includes('MLS')).length }
    ];

    // Filter cards based on selected lead source
    const filterCardsBySource = (cards) => {
      if (selectedLeadSource === 'all') return cards;
      
      return cards.filter(card => {
        if (!card.source) return false;
        const source = card.source.toLowerCase();
        
        if (selectedLeadSource === 'phase_0') return source.includes('phase 0');
        if (selectedLeadSource === 'phase_1') return source.includes('phase 1');
        if (selectedLeadSource === 'phase_2') return source.includes('phase 2');
        if (selectedLeadSource === 'phase_3') return source.includes('phase 3');
        if (selectedLeadSource === 'phase_4') return source.includes('phase 4');
        if (selectedLeadSource === 'phase_5') return source.includes('phase 5');
        if (selectedLeadSource === 'seasonal') return source.includes('seasonal');
        if (selectedLeadSource === 'mls') return source.includes('mls');
        
        return false;
      });
    };

    return (
      <div className="h-full flex flex-col bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => setActiveBoard(null)} className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowRight className="rotate-180" size={20} />
            </button>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-800">Sales & Estimates Board</h2>
              <p className="text-sm text-gray-500">Central pipeline for all successful marketing leads</p>
            </div>
          </div>

          {/* Lead Source Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <span className="text-xs font-bold text-slate-600 uppercase mr-2 whitespace-nowrap">Filter by Source:</span>
            {leadSources.map(source => (
              <button
                key={source.id}
                onClick={() => setSelectedLeadSource(source.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm whitespace-nowrap transition-all ${
                  selectedLeadSource === source.id
                    ? `${source.color} text-white shadow-lg scale-105`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{source.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  selectedLeadSource === source.id
                    ? 'bg-white/30'
                    : 'bg-slate-200'
                }`}>
                  {source.count}
                </span>
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex-1 overflow-x-auto p-6">
          <div className="flex gap-4 h-full min-w-max">
            {SALES_COLUMNS.map(col => {
              const colCards = filterCardsBySource(SALES_CARDS.filter(c => c.status === col.id));
              return (
                <div key={col.id} className="w-80 flex flex-col h-full rounded-xl bg-slate-200/50 border border-slate-200">
                  <div className={`p-3 rounded-t-xl border-b border-slate-200 flex justify-between items-center ${col.color}`}>
                    <div>
                      <span className="font-bold text-sm">{col.label}</span>
                      {col.sub && <div className="text-[10px] opacity-70">{col.sub}</div>}
                    </div>
                    <span className="bg-white/50 px-2 py-0.5 rounded text-xs font-bold">{colCards.length}</span>
                  </div>
                  <div className="p-2 flex-1 overflow-y-auto space-y-2">
                    {colCards.map(card => (
                      <SalesCard 
                        key={card.id} 
                        data={card} 
                        onClick={onOpenCRM}
                        onTakeoffClick={onOpenTakeoff}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Digital Marketing Board View
  if (activeBoard === 'digital_marketing') {
    const totalLeads = DIGITAL_MARKETING_CARDS.reduce((sum, c) => sum + c.leads, 0);
    const totalQualified = DIGITAL_MARKETING_CARDS.reduce((sum, c) => sum + c.qualified, 0);
    const totalConverted = DIGITAL_MARKETING_CARDS.reduce((sum, c) => sum + c.converted, 0);
    const avgCPL = Math.round(DIGITAL_MARKETING_CARDS.reduce((sum, c) => sum + parseInt(c.cpl.replace('$', '')), 0) / DIGITAL_MARKETING_CARDS.length);
    
    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-rose-50 via-white to-orange-50">
        {/* Header with Stats */}
        <div className="bg-white border-b shadow-sm">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setActiveBoard(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <ArrowRight className="rotate-180" size={20} />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Megaphone size={28} className="text-rose-600" />
                  Digital Marketing Command Center
                </h2>
                <p className="text-sm text-gray-500">Construction lead generation & phase-targeted campaigns</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg font-bold hover:bg-rose-700 transition-all">
                <Plus size={18} />
                New Campaign
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 transition-all">
                <Filter size={18} />
                Filter
              </button>
            </div>
          </div>
          
          {/* Quick Stats Bar */}
          <div className="px-6 pb-4 grid grid-cols-6 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <div className="text-xs font-bold text-blue-600 uppercase mb-1">Total Campaigns</div>
              <div className="text-2xl font-bold text-blue-900">{DIGITAL_MARKETING_CARDS.length}</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
              <div className="text-xs font-bold text-green-600 uppercase mb-1">Total Leads</div>
              <div className="text-2xl font-bold text-green-900">{totalLeads}</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
              <div className="text-xs font-bold text-orange-600 uppercase mb-1">Qualified</div>
              <div className="text-2xl font-bold text-orange-900">{totalQualified}</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
              <div className="text-xs font-bold text-orange-600 uppercase mb-1">Converted</div>
              <div className="text-2xl font-bold text-orange-900">{totalConverted}</div>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-xl border border-pink-200">
              <div className="text-xs font-bold text-pink-600 uppercase mb-1">Avg CPL</div>
              <div className="text-2xl font-bold text-pink-900">${avgCPL}</div>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-xl border border-teal-200">
              <div className="text-xs font-bold text-teal-600 uppercase mb-1">Avg ROI</div>
              <div className="text-2xl font-bold text-teal-900">425%</div>
            </div>
          </div>
        </div>
        
        {/* Main Content - Kanban with Enhanced Cards */}
        <div className="flex-1 overflow-x-auto p-6">
          <div className="flex gap-4 h-full min-w-max">
            {KANBAN_COLUMNS.map(col => {
              const colCards = DIGITAL_MARKETING_CARDS.filter(c => c.status === col.id);
              return (
                <div key={col.id} className="w-96 flex flex-col h-full rounded-xl bg-white shadow-lg border border-gray-200">
                  <div className={`p-4 rounded-t-xl border-b border-gray-200 flex justify-between items-center ${col.color}`}>
                    <div>
                      <span className="font-bold text-sm">{col.label}</span>
                      {col.sub && <span className="block text-[10px] opacity-70 mt-0.5">{col.sub}</span>}
                    </div>
                    <span className="bg-white/80 px-3 py-1 rounded-full text-sm font-bold shadow-sm">{colCards.length}</span>
                  </div>
                  <div className="p-3 flex-1 overflow-y-auto space-y-3">
                    {colCards.map(card => (
                      <div 
                        key={card.id}
                        onClick={() => onOpenCRM && onOpenCRM(card)}
                        className="bg-gradient-to-br from-white to-gray-50 p-4 rounded-xl shadow-md border-2 border-rose-200 cursor-pointer hover:shadow-xl hover:border-rose-400 hover:scale-[1.02] transition-all group relative overflow-hidden"
                      >
                        {/* Top accent bar */}
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 via-pink-500 to-orange-500"></div>
                        
                        {/* Campaign Type Badge */}
                        <div className="flex justify-between items-start mb-3">
                          <span className="text-[10px] font-bold bg-rose-100 text-rose-700 px-2 py-1 rounded-full uppercase tracking-wide">
                            {card.type}
                          </span>
                          <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            {card.adSpend}
                          </span>
                        </div>
                        
                        {/* Campaign Name */}
                        <h4 className="font-bold text-base text-gray-900 mb-2 group-hover:text-rose-700 transition-colors leading-tight">
                          {card.name}
                        </h4>
                        
                        {/* Campaign Description */}
                        <p className="text-[11px] text-gray-600 mb-3 font-medium">
                          {card.campaign}
                        </p>
                        
                        {/* Business & Contact Info */}
                        <div className="bg-white p-3 rounded-lg border border-gray-200 mb-3 space-y-1.5">
                          <div className="flex items-start gap-2">
                            <Building size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold text-gray-800 truncate">{card.business}</div>
                              <div className="text-[10px] text-gray-500">{card.builder}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <UserCheck size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-700 truncate">{card.contact}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-600">{card.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="text-[10px] text-gray-600 truncate">{card.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-600 truncate">{card.location}</span>
                          </div>
                        </div>
                        
                        {/* Target Phase Badge */}
                        <div className="mb-3">
                          <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-100 to-orange-100 px-3 py-1.5 rounded-lg border border-indigo-300">
                            <Layers size={12} className="text-indigo-600" />
                            <span className="text-[10px] font-bold text-indigo-700 uppercase">{card.targetPhase}</span>
                          </div>
                        </div>
                        
                        {/* Performance Metrics Grid */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          <div className="bg-blue-50 p-2 rounded-lg border border-blue-200 text-center">
                            <div className="text-[10px] text-blue-600 font-bold uppercase">Leads</div>
                            <div className="text-lg font-bold text-blue-900">{card.leads}</div>
                          </div>
                          <div className="bg-green-50 p-2 rounded-lg border border-green-200 text-center">
                            <div className="text-[10px] text-green-600 font-bold uppercase">Qualified</div>
                            <div className="text-lg font-bold text-green-900">{card.qualified}</div>
                          </div>
                          <div className="bg-orange-50 p-2 rounded-lg border border-orange-200 text-center">
                            <div className="text-[10px] text-orange-600 font-bold uppercase">Won</div>
                            <div className="text-lg font-bold text-orange-900">{card.converted}</div>
                          </div>
                        </div>
                        
                        {/* ROI & CPL */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-2 rounded-lg border border-teal-200">
                            <div className="text-[10px] text-teal-600 font-bold uppercase">ROI</div>
                            <div className="text-base font-bold text-teal-900">{card.roi}</div>
                          </div>
                          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-2 rounded-lg border border-orange-200">
                            <div className="text-[10px] text-orange-600 font-bold uppercase">CPL</div>
                            <div className="text-base font-bold text-orange-900">{card.cpl}</div>
                          </div>
                        </div>
                        
                        {/* CTR & Impressions */}
                        <div className="bg-gray-100 p-2 rounded-lg mb-3 text-center">
                          <div className="flex justify-around items-center">
                            <div>
                              <div className="text-[9px] text-gray-500 uppercase font-bold">CTR</div>
                              <div className="text-sm font-bold text-gray-800">{card.ctr}</div>
                            </div>
                            <div className="w-px h-6 bg-gray-300"></div>
                            <div>
                              <div className="text-[9px] text-gray-500 uppercase font-bold">Impressions</div>
                              <div className="text-sm font-bold text-gray-800">{card.impressions}</div>
                            </div>
                            <div className="w-px h-6 bg-gray-300"></div>
                            <div>
                              <div className="text-[9px] text-gray-500 uppercase font-bold">Clicks</div>
                              <div className="text-sm font-bold text-gray-800">{card.clicks}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Tags */}
                        {card.tags && card.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {card.tags.map((tag, idx) => (
                              <span key={idx} className="text-[9px] font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Footer - Next Follow-up & Last Action */}
                        <div className="border-t border-gray-200 pt-2 flex justify-between items-center text-[10px]">
                          <div className="flex items-center gap-1 text-gray-500">
                            <Clock size={10} />
                            <span>Last: {card.lastAction}</span>
                          </div>
                          <div className="flex items-center gap-1 text-rose-600 font-bold">
                            <CalendarDays size={10} />
                            <span>{card.nextFollowUp.split(',')[1]}</span>
                          </div>
                        </div>
                        
                        {/* Hover Action Hint */}
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="bg-rose-600 text-white text-[9px] font-bold px-2 py-1 rounded shadow-lg">
                            Click for CRM
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Logistics / Delivery Board with Map View
  if (activeBoard === 'delivery') {
    return (
      <DeliveryBoard 
        onClose={() => setActiveBoard(null)} 
        deliveryItems={DELIVERY_ITEMS}
        deliveryRoutes={DELIVERY_ROUTES}
      />
    );
  }

  if (activeBoard) {
    const board = SERVICE_BOARDS.find(b => b.id === activeBoard);
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-6 py-4 flex items-center gap-4">
          <button onClick={() => setActiveBoard(null)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowRight className="rotate-180" size={20} />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{board.name}</h2>
            <p className="text-sm text-gray-500">Construction pipeline board</p>
          </div>
        </div>
        
        <div className="flex-1 overflow-x-auto p-6">
          <div className="flex gap-4 h-full min-w-max">
            {KANBAN_COLUMNS.map(col => {
              const colCards = MOCK_CARDS.filter(c => c.status === col.id);
              return (
                <div key={col.id} className="w-72 flex flex-col h-full rounded-xl bg-gray-200/50 border border-gray-200/60">
                  <div className={`p-3 rounded-t-xl border-b border-gray-200/60 flex justify-between items-center ${col.color}`}>
                    <div>
                      <span className="font-bold text-sm">{col.label}</span>
                      {col.sub && <span className="block text-[10px] opacity-70">{col.sub}</span>}
                    </div>
                    <span className="bg-white/50 px-2 py-0.5 rounded text-xs font-bold">{colCards.length}</span>
                  </div>
                  <div className="p-2 flex-1 overflow-y-auto space-y-2">
                    {colCards.map(card => (
                      <KanbanCard 
                        key={card.id} 
                        data={card} 
                        onClick={() => {
                          // Use CRM modal for all boards except delivery only
                          if (activeBoard !== 'delivery') {
                            onOpenCRM && onOpenCRM(card);
                          } else {
                            onCardClick && onCardClick(card);
                          }
                        }} 
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-y-auto p-8">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Services & Pipelines</h2>
          <p className="text-gray-500 mt-1">Select a board to manage leads, logistics, or internal sales.</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg font-bold shadow-lg hover:bg-gray-800">
          <Plus size={18} /> Create Custom Board
        </button>
      </div>
      
      <h3 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">Central Sales Hub</h3>
      <div className="grid grid-cols-1 mb-10">
        <button onClick={() => setActiveBoard('sales_estimates')} className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl shadow-sm border border-green-200 hover:shadow-xl hover:border-green-400 transition-all group text-left relative overflow-hidden flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="p-4 bg-white rounded-2xl shadow-sm group-hover:scale-110 transition-transform">
              <DollarSign size={32} className="text-green-600" />
            </div>
            <div>
              <h4 className="text-2xl font-bold text-gray-800 mb-1">Sales & Estimates</h4>
              <p className="text-sm text-gray-600">Unified pipeline for all successful marketing leads</p>
            </div>
          </div>
          <div className="flex gap-8 pr-8">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-800">{SALES_CARDS.length}</div>
              <div className="text-xs font-bold text-gray-400 uppercase">Active</div>
            </div>
          </div>
        </button>
      </div>
      
      <h3 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">Construction Waterfall (Phases)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {SERVICE_BOARDS.filter(b => b.type === 'pipeline').map(board => (
          <button
            key={board.id}
            onClick={() => setActiveBoard(board.id)}
            className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all group text-left relative overflow-hidden"
          >
            <div className={`absolute top-0 left-0 w-1 h-full ${board.color.replace('border-', 'bg-')}`}></div>
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-gray-50 rounded-xl group-hover:bg-blue-50 transition-colors">
                <Layers size={24} className="text-gray-400 group-hover:text-blue-600" />
              </div>
              <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2 py-1 rounded">
                {MOCK_CARDS.length} Leads
              </span>
            </div>
            <h4 className="text-lg font-bold text-gray-800 group-hover:text-blue-700 transition-colors">{board.name}</h4>
            <p className="text-xs text-gray-400 mt-1">Sequential progression logic</p>
          </button>
        ))}
      </div>
      
      <h3 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">Specialized & Operations</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SERVICE_BOARDS.filter(b => ['seasonal', 'mls', 'delivery', 'marketing'].includes(b.type)).map(board => {
          const Icon = board.icon;
          return (
            <button
              key={board.id}
              onClick={() => setActiveBoard(board.id)}
              className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-orange-300 transition-all group text-left"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-3 rounded-xl ${board.type === 'marketing' ? 'bg-rose-50' : 'bg-orange-50'}`}>
                  <Icon size={24} className={board.type === 'marketing' ? 'text-rose-600' : 'text-orange-600'} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-800">{board.name}</h4>
                  <span className={`text-[10px] uppercase font-bold ${board.type === 'marketing' ? 'text-rose-600' : 'text-orange-600'}`}>
                    {board.type === 'delivery' ? 'Operations' : board.type === 'marketing' ? 'Marketing' : 'Special'}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {board.type === 'delivery' ? 'Logistics and delivery management.' : board.type === 'marketing' ? 'Digital campaigns and lead generation.' : 'Non-linear workflow enabled.'}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ==================== MAIN APP COMPONENT ====================

// ==================== MAIN APP COMPONENT ====================

export default function BlueJaysOnAirApp() {
  // --- Auth gate (Phase 1.5 Step 1) ---
  const [authUser, setAuthUser] = useState(null);
  const [authRole, setAuthRole] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthUser(u);
      if (u) { try { setAuthRole(await ensureUserRecord(u)); } catch (e) { console.error("Role lookup failed:", e); setAuthRole('pending'); } }
      else setAuthRole(null);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const [role, setRole] = useState(null);
  const [activeCategory, setActiveCategory] = useState('dashboard');
  const [activeSubBoard, setActiveSubBoard] = useState(null);
  const [expandedMenu, setExpandedMenu] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showLeadsAnalytics, setShowLeadsAnalytics] = useState(false);
  const [showCRMModal, setShowCRMModal] = useState(false);
  const [crmCardData, setCRMCardData] = useState(null);
  const [showTakeoffModal, setShowTakeoffModal] = useState(false);
  const [takeoffCardData, setTakeoffCardData] = useState(null);
  
  // Networking Filters
  const [activeNationalityFilter, setActiveNationalityFilter] = useState('all');
  const [activeTradeFilter, setActiveTradeFilter] = useState('all');
  
  // Inbox state
  const [showGlobalInbox, setShowGlobalInbox] = useState(false);
  
  console.log('🟣🟣🟣 APP LEVEL - showLeadsAnalytics:', showLeadsAnalytics);
  
  // LEGO CONFIGURATION STATE (Modular B2B Setup)
  const [legoConfig, setLegoConfig] = useState({
    branding: { logo: null, primaryColor: '#2563eb' },
    infrastructure: { voipProvisioned: false, emailDnsVerified: false },
    modules: [] // The "Lego" blocks: ['Telemarketing', 'Logistics', 'MLS_Feed']
  });

  const handleLegoLaunch = (clientId) => {
    console.log(`🚀 Generating environment for Client: ${clientId}`);
    console.log('Selected Modules:', legoConfig.modules);
    // Logic to inject the selected modules into the Service Boards array
    alert(`Service board launched for client with modules: ${legoConfig.modules.join(', ')}`);
  };

  const toggleExpand = (id) => {
    setExpandedMenu(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleMenuClick = (item) => {
    setActiveCategory(item.id);
    setActiveSubBoard(null);
  };

  const handleSubItemClick = (boardName) => {
    setActiveSubBoard(boardName);
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  const handleOpenCRMModal = (card) => {
    setCRMCardData(card);
    setShowCRMModal(true);
  };

  const handleOpenTakeoffModal = (card) => {
    setTakeoffCardData(card);
    setShowTakeoffModal(true);
  };

  const handleCloseTakeoffModal = () => {
    setShowTakeoffModal(false);
    setTakeoffCardData(null);
  };

  const handleCloseCRMModal = () => {
    setShowCRMModal(false);
    setCRMCardData(null);
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const handleToggleLeadsAnalytics = () => {
    console.log('🟡🟡🟡 BlueJaysOnAirApp - handleToggleLeadsAnalytics called');
    console.log('🟡 Current showLeadsAnalytics:', showLeadsAnalytics);
    setShowLeadsAnalytics(!showLeadsAnalytics);
    console.log('🟡 After setState (may not reflect immediately):', !showLeadsAnalytics);
  };

  const handleCloseLeadsAnalytics = () => {
    console.log('🔴🔴🔴 handleCloseLeadsAnalytics called');
    setShowLeadsAnalytics(false);
  };

  let currentView;
  if (activeCategory === 'dashboard') {
    currentView = <DashboardView onNavigate={setActiveCategory} showLeadsAnalytics={showLeadsAnalytics} setShowLeadsAnalytics={setShowLeadsAnalytics} />;
  } else if (activeCategory === 'data_collection') {
    currentView = <InPersonActionView />;
  } else if (activeCategory === 'management') {
    currentView = (
      <ManagementView
        activeSubBoard={activeSubBoard}
        setActiveSubBoard={setActiveSubBoard}
        onOpenCRM={handleOpenCRMModal}
      />
    );
  } else if (activeCategory === 'sales_pr') {
    currentView = (
      <SalesPRView
        activeSubBoard={activeSubBoard}
        setActiveSubBoard={setActiveSubBoard}
        onCardClick={handleCardClick}
        onOpenCRM={handleOpenCRMModal}
        onOpenTakeoff={handleOpenTakeoffModal}
        activeNationalityFilter={activeNationalityFilter}
        setActiveNationalityFilter={setActiveNationalityFilter}
        activeTradeFilter={activeTradeFilter}
        setActiveTradeFilter={setActiveTradeFilter}
      />
    );
  } else if (activeCategory === 'services') {
    currentView = <ServicesViewEnhanced onCardClick={handleCardClick} onOpenCRM={handleOpenCRMModal} onOpenTakeoff={handleOpenTakeoffModal} />;
  } else if (activeCategory === 'users_files') {
    currentView = <UsersFilesView activeSubBoard={activeSubBoard} />;
  }

  // --- Auth gate ---
  if (authLoading) return <div className="h-screen flex items-center justify-center bg-gray-50 text-gray-500 font-bold">Loading…</div>;
  if (!authUser) return <LoginScreen />;
  if (authRole === 'pending' || !authRole) return <PendingScreen email={authUser.email} />;
  if (authRole === 'collector') return <div className="h-screen w-full"><MapEngine forceRole="collector" onExit={() => signOut(auth)} /></div>;
  if (authRole !== 'manager') return <WorkspaceComingSoon role={authRole} />;

  return (
    <div className="h-screen flex flex-col bg-white">
      <div className="bg-[#0F1520] text-white px-4 py-1.5 flex justify-between items-center shadow-md z-50 shrink-0">
        <div className="flex items-center gap-3">
          <BrandLogo height={26} dark />
          <p className="text-[10px] text-gray-400 leading-tight hidden sm:block">CRM &amp; Operations</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => signOut(auth)} title="Sign out" className="px-3 py-1 hover:bg-white/10 rounded-lg transition-colors text-xs font-bold">Sign out</button>
          <button className="p-2 hover:bg-blue-700 rounded-lg transition-colors relative">
            <Bell size={20} />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          <button className="p-2 hover:bg-blue-700 rounded-lg transition-colors">
            <Users size={20} />
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors md:hidden"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileMenuOpen ? 'block' : 'hidden'
          } md:block w-64 bg-slate-50 border-r border-gray-200 overflow-y-auto flex-shrink-0`}
        >
          <nav className="p-4 space-y-2">
            {/* Inbox - Special Section */}
            <button
              onClick={() => setShowGlobalInbox(true)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg font-bold text-sm transition-all bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md hover:shadow-lg mb-4 relative"
            >
              <div className="flex items-center gap-3">
                <Inbox size={18} />
                <div className="text-left">
                  <div>Inbox</div>
                  <div className="text-[10px] opacity-80">Email & Internal</div>
                </div>
              </div>
              <span className="bg-white/30 text-white text-xs font-bold px-2 py-0.5 rounded-full">3</span>
            </button>
            
            {MENU_STRUCTURE.map(item => (
              <div key={item.id}>
                <button
                  onClick={() => {
                    if (item.boards && item.boards.length > 0) {
                      toggleExpand(item.id);
                    } else {
                      handleMenuClick(item);
                    }
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                    activeCategory === item.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={18} />
                    <div className="text-left">
                      <div>{item.label}</div>
                      {item.subLabel && (
                        <div className="text-[10px] opacity-70">{item.subLabel}</div>
                      )}
                    </div>
                  </div>
                  {item.boards && item.boards.length > 0 && (
                    expandedMenu[item.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  )}
                </button>
                {item.boards && item.boards.length > 0 && expandedMenu[item.id] && (
                  <div className="ml-6 mt-2 space-y-1">
                    {item.boards.map(board => (
                      <button
                        key={board}
                        onClick={() => {
                          handleMenuClick(item);
                          handleSubItemClick(board);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                          activeSubBoard === board
                            ? 'bg-blue-100 text-blue-700 font-bold'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {board}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-hidden">
          {currentView}
        </div>
      </div>

      {showCRMModal && crmCardData && (
        <CRMCardModal
          card={crmCardData}
          onClose={handleCloseCRMModal}
          onUpdate={(updatedCard) => {
            // Handle card update logic here
            console.log('Card updated:', updatedCard);
            handleCloseCRMModal();
          }}
        />
      )}

      {showTakeoffModal && takeoffCardData && (
        <TakeoffEstimateModal
          card={takeoffCardData}
          onClose={handleCloseTakeoffModal}
          onSave={(estimateData) => {
            // Handle estimate save logic here
            console.log('Estimate saved:', estimateData);
            handleCloseTakeoffModal();
          }}
        />
      )}

      {showGlobalInbox && (
        <GlobalInbox onClose={() => setShowGlobalInbox(false)} />
      )}
    </div>
  );
}